import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import { useCapaAccess, canUpdateCapaRecord, canChangeCapaStage } from "./capaAccess";
import {
  fetchCapaById,
  fetchCapaActionItems,
  fetchCapaApproverRecords,
  fetchCapaAuditLogForCapa,
  fetchCapaConfig,
  executeCapaTransition,
  saveCapa,
  createCapaActionItem,
  createCapaApproverRecord,
  updateCapaActionItemStatus,
  updateCapaApproverRecordStatus,
  deleteCapaActionItem,
  deleteCapaApproverRecord,
  fetchCapaImplementationTasks,
  createCapaImplementationTask,
  updateCapaImplementationTaskStatus,
  deleteCapaImplementationTask,
  fetchCapaVerificationRequests,
  createCapaVerificationRequest,
  deleteCapaVerificationRequest,
  submitCapaVerificationResponse,
  verificationRequestAssigneeMatches,
  capaModuleOptsFromDetail,
  normalizeCapaApiDateYmd,
} from "./capaService";
import { mapCapaStaffOptions } from "./capaStaffUtils";
import {
  CAPA_STEPPER_STEPS,
  WORKFLOW_ACTIONS,
  capaDetailsAreComplete,
  getCapaRoles,
  getAvailableWorkflowActions,
  getAdvanceButtonLabel,
  getStageIndex,
  isCapaImplementationPhase,
  normalizeCapaLifecycleStatusForUi,
} from "./capaWorkflow";
import { CAPA_LIFECYCLE_PHASES } from "./capaPhasesMeta";
import CapaStageStepper from "./CapaStageStepper";
import Dropdown from "../../common/dropDown";
import TextEditor from "../../common/TextEditor/TextEditor";
import DatePicker from "react-datepicker";
import DataTable from "../../common/dataTable";
import HoverPreview from "../../common/HoverPreview/HoverPreview";
import { getAvatarColor, getInitials, stripHtml } from "../../../utils/avatarUtils";
import {
  CAPA_ISF_TEMPLATE_CATEGORIES,
  CAPA_RISK_OPTIONS,
  CAPA_RCA_METHOD_OPTIONS,
  CAPA_IMPLEMENTATION_STATUS_OPTIONS,
  CAPA_IMPLEMENTATION_BLOCKING_FOR_VERIFICATION,
  CAPA_VERIFICATION_OUTCOME_OPTIONS,
  getCapaStatusBadgeStyle,
  normalizeIsfTemplateCategoryForApi,
} from "./capaConstants";
import {
  resolveCapaModuleDropdownOptions,
  toCapaModuleOption,
  capaModuleDropdownOptionsWithAll,
  CAPA_MODULE_OPTION_ALL,
  isCapaModuleAllValue,
  sessionModuleOptionForStoredCapa,
} from "./capaSessionModules";
import api from "../../../services/apiClient";
import API_ENDPOINTS from "../../../config/apiEndpoints";
import utils from "../../../utils/utils";
import { useLoader } from "../../../context/LoaderContext";
import ConfirmModal from "../../common/ConfirmModal";
import useConfirmModal from "../../../hooks/useConfirmModal";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import Sidemodal from "../../common/sidemodal/sidemodal";
import { openAuthModal } from "../../common/authmodal/authmodalservice";
import "quill/dist/quill.snow.css";
import "react-datepicker/dist/react-datepicker.css";
import FileUpload from "../../common/fileupload/FileUpload";
import "../../common/fileupload/FileUpload.css";
import "./CapaPages.scss";
import "./CapaCreate.scss";

function optionFromValue(options, value) {
  const v = value == null ? "" : String(value);
  return options.find((o) => o.value === v) || options[0];
}

function implStatusOptionFromValue(val) {
  return (
    CAPA_IMPLEMENTATION_STATUS_OPTIONS.find((o) => o.value === val) ||
    CAPA_IMPLEMENTATION_STATUS_OPTIONS.find((o) => o.value === "Not Started")
  );
}

function rcaMethodOptionFromStored(raw, options = CAPA_RCA_METHOD_OPTIONS) {
  if (raw == null || String(raw).trim() === "") {
    return options[0] || CAPA_RCA_METHOD_OPTIONS[0];
  }
  const s = String(raw).trim();
  const byValue = options.find((o) => o.value === s);
  if (byValue) return byValue;
  const byLabel = options.find((o) => o.label === s);
  if (byLabel) return byLabel;
  return { value: s, label: s };
}

const CAPA_HTML_TAG_RE = /<[^>]+>/;

/** Props for HoverPreview when stored text may be Quill HTML. */
function capaRichHoverPreviewProps(raw) {
  const str = String(raw ?? "").trim();
  if (!str || str === "—") {
    return { content: str || "—", isHtml: false, plainTextForPreview: undefined };
  }
  const looksHtml = CAPA_HTML_TAG_RE.test(str);
  const plain = stripHtml(str);
  return {
    content: str,
    isHtml: looksHtml,
    plainTextForPreview: looksHtml ? plain : undefined,
  };
}

/** Read-only display for stored rich text in modals. */
function renderCapaStoredRichText(raw) {
  const s = String(raw ?? "").trim();
  if (!s) return "—";
  if (CAPA_HTML_TAG_RE.test(s)) {
    return (
      <div className="ql-snow">
        <div
          className="ql-editor"
          dangerouslySetInnerHTML={{ __html: s }}
        />
      </div>
    );
  }
  return s;
}

const MAX_EVIDENCE_FILES = 20;

function parseEvidenceFiles(raw) {
  if (!raw) return [];
  try {
    const p = typeof raw === "string" ? JSON.parse(raw) : raw;
    return Array.isArray(p) ? p : [];
  } catch {
    return [];
  }
}

function normalizeCapaRouteToken(value) {
  return decodeURIComponent(String(value || "").trim())
    .replace(/[{}]/g, "")
    .toLowerCase();
}

function isCapaGuidToken(value) {
  const s = String(value || "").trim().replace(/[{}]/g, "");
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    s,
  );
}

/** Pre-audit-shape rows stored only the request GUID in NewValue (no verifier HTML). */
function isVerificationAssignedLegacyAuditRow(rawAction, fieldName, newValueRaw) {
  const a = String(rawAction || "").trim();
  const field = String(fieldName || "").trim();
  const nv = String(newValueRaw ?? "").trim();
  return (
    a === "VerificationRequestAssigned" &&
    field === "VerificationRequestId" &&
    nv.length <= 44 &&
    isCapaGuidToken(nv)
  );
}

function normalizeEvidenceFilesForCompare(files) {
  const list = Array.isArray(files) ? files : [];
  return JSON.stringify(
    [...list]
      .map((f) => ({
        id: f.id != null ? String(f.id) : "",
        name: f.name != null ? String(f.name) : "",
        size: f.size != null ? Number(f.size) : 0,
        type: f.type != null ? String(f.type) : "",
        dataUrl: f.dataUrl != null ? String(f.dataUrl) : "",
        notes: f.notes != null ? String(f.notes) : "",
      }))
      .sort((a, b) => a.id.localeCompare(b.id)),
  );
}

/** Investigation saves must not send RCA/closure fields (avoids bogus audit rows). */
function omitRcaAndClosureFromCapaPayload(payload) {
  const out = { ...payload };
  [
    "rootCauseSummary",
    "rcaMethod",
    "rcaEvidenceFiles",
    "closureRemarks",
    "closureEvidenceFiles",
    "RootCauseSummary",
    "RcaMethod",
    "RcaEvidenceFiles",
    "ClosureRemarks",
    "ClosureEvidenceFiles",
  ].forEach((k) => delete out[k]);
  return out;
}

/** RCA-only saves must not resend investigation or closure payloads. */
function omitInvestigationAndClosureFromCapaPayload(payload) {
  const out = { ...payload };
  [
    "investigationSummary",
    "supportingEvidence",
    "supportingEvidenceFiles",
    "InvestigationSummary",
    "SupportingEvidence",
    "SupportingEvidenceFiles",
    "closureRemarks",
    "closureEvidenceFiles",
    "ClosureRemarks",
    "ClosureEvidenceFiles",
  ].forEach((k) => delete out[k]);
  return out;
}

/** Closure attachment saves must not resend RCA/investigation (unchanged slices). */
function omitRcaAndInvestigationFromCapaPayload(payload) {
  const out = { ...payload };
  [
    "investigationSummary",
    "supportingEvidence",
    "supportingEvidenceFiles",
    "InvestigationSummary",
    "SupportingEvidence",
    "SupportingEvidenceFiles",
    "rootCauseSummary",
    "rcaMethod",
    "rcaEvidenceFiles",
    "RootCauseSummary",
    "RcaMethod",
    "RcaEvidenceFiles",
  ].forEach((k) => delete out[k]);
  return out;
}

function formatFileSize(bytes) {
  if (bytes == null || Number.isNaN(bytes)) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function parseYmdToDate(value) {
  if (!value) return null;
  const d = new Date(`${value}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDateToYmd(value) {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return "";
  const y = value.getFullYear();
  const m = String(value.getMonth() + 1).padStart(2, "0");
  const d = String(value.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** DD/MM/YYYY for verification cards (due date / created date). */
function formatVerificationDateOnly(val) {
  const d = utils.parseDateInput(val);
  if (!d) return "—";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

function getActionItemStatusLabel(raw) {
  const v = String(raw || "").trim().toLowerCase();
  if (!v || v === "pending" || v === "n/a" || v === "na") return "N/A";
  if (v === "approved" || v === "accepted") return "Accepted";
  if (v === "rejected") return "Rejected";
  return raw;
}

/** Read-only Details step: label + value tile (no disabled inputs). */
function CapaDetailKvTile({ iconClass, label, children }) {
  return (
    <div className="capa-detail-kv-tile">
      <div className="capa-detail-kv-tile__icon" aria-hidden>
        <i className={iconClass} />
      </div>
      <div className="capa-detail-kv-tile__main">
        <div className="capa-detail-kv-tile__label">{label}</div>
        <div className="capa-detail-kv-tile__value">{children}</div>
      </div>
    </div>
  );
}

function truncateMiddleDisplay(text, maxLen) {
  const s = String(text || "").trim();
  if (!s) return "";
  if (s.length <= maxLen) return s;
  const keep = Math.max(1, maxLen - 3);
  const left = Math.floor(keep / 2);
  const right = Math.ceil(keep / 2);
  return s.slice(0, left) + "..." + s.slice(s.length - right);
}

function escapeAuditHtml(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function splitInvestigationEvidenceAudit(raw) {
  return String(raw || "")
    .split(/\s*\|\s*/)
    .map((x) => x.trim())
    .filter(Boolean);
}

function parseInvestigationEvidenceAuditSegment(segment) {
  const s = String(segment || "").trim();
  const fileMatch = s.match(/^File:\s*(.+?)(?:;\s*Notes:\s*(.*))?$/i);
  if (fileMatch) {
    const fileName = (fileMatch[1] || "").trim();
    const notes = (fileMatch[2] || "").trim();
    return { kind: "file", fileName, notes };
  }
  const notesMatch = s.match(/^Notes:\s*(.*)$/i);
  if (notesMatch) {
    const notes = (notesMatch[1] || "").trim();
    return { kind: "notes", notes };
  }
  return { kind: "text", text: s };
}

/** One API segment: notes-only = plain + same hover; file = filename in badge, hover rules per wireframe. */
function InvestigationEvidenceAuditSegment({ segment, toneClass }) {
  const p = parseInvestigationEvidenceAuditSegment(segment);
  const cls = ["capa-investigation-audit-hover", toneClass || ""]
    .filter(Boolean)
    .join(" ");
  const esc = escapeAuditHtml;

  if (p.kind === "notes") {
    const notes = String(p.notes || "").trim();
    if (!notes) return <span className="text-muted">—</span>;
    return (
      <HoverPreview
        content={notes}
        plainTextForPreview={notes}
        isHtml={false}
        previewLength={56}
        appendEllipsis
        maxWidth={280}
        maxHeight={220}
        className={cls}
      />
    );
  }

  if (p.kind === "file") {
    const name = String(p.fileName || "").trim() || "(attached)";
    const notes = String(p.notes || "").trim();

    if (!notes) {
      const trunc = truncateMiddleDisplay(name, 26);
      const anchorBadge = `<span class="badge bg-dark text-white capa-inv-ev-badge">${esc(trunc)}</span>`;
      const popHtml = `<div class="capa-inv-ev-pop text-start"><span class="badge bg-dark text-white capa-inv-ev-badge">${esc(name)}</span></div>`;
      return (
        <HoverPreview
          content={popHtml}
          anchorHtml={anchorBadge}
          isHtml
          plainTextForPreview={name}
          previewLength={18}
          appendEllipsis
          maxWidth={300}
          maxHeight={140}
          className={cls}
        />
      );
    }

    const truncName = truncateMiddleDisplay(name, 20);
    const truncNotes = truncateMiddleDisplay(notes, 32);
    const anchorPair = `<span class="capa-inv-ev-anchor-pair d-inline-flex align-items-center gap-1 min-w-0"><span class="badge bg-dark text-white text-truncate capa-inv-ev-badge" style="max-width:9rem">${esc(truncName)}</span><span class="text-muted flex-shrink-0">|</span><span class="text-truncate min-w-0" style="max-width:10rem">${esc(truncNotes)}</span></span>`;
    const popHtml = `<div class="capa-inv-ev-pop text-start"><div><span class="badge bg-dark text-white text-break">${esc(name)}</span></div><div class="small mt-2 text-break capa-inv-ev-pop-notes">${esc(notes)}</div></div>`;
    const plainForLen = `${name} | ${notes}`;
    return (
      <HoverPreview
        content={popHtml}
        anchorHtml={anchorPair}
        isHtml
        plainTextForPreview={plainForLen}
        previewLength={plainForLen.length + 1}
        appendEllipsis={false}
        maxWidth={300}
        maxHeight={260}
        className={cls}
      />
    );
  }

  const t = String(p.text || "").trim();
  if (!t) return <span className="text-muted">—</span>;
  return (
    <HoverPreview
      content={t}
      plainTextForPreview={t}
      isHtml={false}
      previewLength={64}
      maxWidth={280}
      maxHeight={200}
      className={cls}
    />
  );
}

/** Renders API audit string (pipe-separated segments). */
function InvestigationEvidenceAuditInline({ raw, toneClass }) {
  const parts = splitInvestigationEvidenceAudit(raw);
  if (parts.length === 0) {
    return <span className="text-muted">—</span>;
  }
  return (
    <div className={`capa-investigation-audit-inline ${toneClass || ""}`}>
      {parts.map((seg, idx) => (
        <React.Fragment key={`${idx}-${seg.slice(0, 24)}`}>
          {idx > 0 ? (
            <span
              className="capa-investigation-audit-inline__sep text-muted px-1"
              aria-hidden
            >
              |
            </span>
          ) : null}
          <InvestigationEvidenceAuditSegment segment={seg} toneClass={toneClass} />
        </React.Fragment>
      ))}
    </div>
  );
}

/**
 * Maps `usp_CAPA_Activity_NEWUI` TransitionStatus audit rows ([Action] = @TransitionAction)
 * to readable titles (raw values are WORKFLOW_ACTIONS.* slugs).
 */
function getWorkflowTransitionAuditDisplay(rawAction, rawFieldName) {
  const action = String(rawAction || "").trim().toLowerCase();
  const field = String(rawFieldName || "").trim();
  if (field !== "Status") return null;
  switch (action) {
    case WORKFLOW_ACTIONS.APPROVE:
      return "Sent for approval (Action planning → Approval)";
    case WORKFLOW_ACTIONS.REJECT:
      return "Rejected at action planning";
    case WORKFLOW_ACTIONS.MARK_COMPLETED:
      return "Sent for implementation (Approval → Implementation)";
    case WORKFLOW_ACTIONS.SUBMIT_ACTION_PLAN:
      return "Submitted action plan (→ Action planning)";
    case WORKFLOW_ACTIONS.SUBMIT_RCA:
      return "Submitted RCA (→ Under RCA)";
    case WORKFLOW_ACTIONS.SUBMIT_INVESTIGATION:
      return "Submitted investigation";
    case WORKFLOW_ACTIONS.SEND_TO_VERIFICATION:
      return "Sent to verification (Implementation → Verification)";
    case WORKFLOW_ACTIONS.SUBMIT_VERIFICATION_EFFECTIVE:
      return "Verification submitted (effective)";
    case WORKFLOW_ACTIONS.SUBMIT_VERIFICATION_NOT_EFFECTIVE:
      return "Verification submitted (not effective)";
    case WORKFLOW_ACTIONS.SEND_TO_CLOSURE:
      return "Sent for closure";
    case WORKFLOW_ACTIONS.COMPLETE_CLOSURE:
      return "CAPA closed";
    case "transitionstatus":
      return "Status changed";
    default:
      return null;
  }
}

/** Same Activity log UI as Details step (right column). */
function CapaActivityLogPanel({ auditLog, panelHeight, flexFill }) {
  const stretchToViewport = Boolean(flexFill && !panelHeight);
  return (
    <div
      className={`capa-detail-summary__activity${
        stretchToViewport ? " capa-detail-summary__activity--flex-fill" : ""
      }`}
      style={
        panelHeight
          ? { height: `${panelHeight}px`, maxHeight: `${panelHeight}px` }
          : undefined
      }
    >
      <div className="capa-detail-summary__activity-head d-flex justify-content-between align-items-center gap-2">
        <span>Activity log</span>
        <Link to="/capa/audit" className="small">
          All CAPA audit records
        </Link>
      </div>
      <div className="capa-detail-summary__activity-table capa-detail-summary__activity-table--force-scroll">
        {auditLog.length === 0 ? (
          <div className="text-muted small px-3 py-2">No audit entries yet.</div>
        ) : (
          <div className="capa-detail-summary__activity-list">
            {auditLog.map((r) => {
              const actorLabel = String(r.UserName || "").trim() || "—";
              const rawAction = String(r.Action || "").trim();
              const rawField = (r.FieldName && String(r.FieldName).trim()) || "";
              const actionDisplay =
                rawAction === "ActionPlanningTaskAccepted"
                  ? "Action planning task accepted"
                  : rawAction === "ActionPlanningTaskRejected"
                    ? "Action planning task rejected"
                    : rawAction === "ImplementationSnapshotAtVerification"
                      ? "Implementation tasks snapshot (sent to Verification)"
                      : rawAction === "VerificationRequestAssigned"
                        ? "Verification request assigned"
                        : rawAction === "VerificationRequestSubmitted"
                          ? "Verification outcome submitted"
                          : rawAction === "VerificationRequestDeleted"
                            ? "Verification request deleted"
                          : getWorkflowTransitionAuditDisplay(rawAction, rawField) ||
                        (rawAction ? rawAction : "Update");
              const fieldDisplay =
                rawField === "InvestigationEvidence"
                  ? "Investigation evidences"
                  : rawField === "RcaEvidence"
                    ? "RCA evidences"
                    : rawField === "VerifierInstructions" ||
                        (rawField === "VerificationRequestId" &&
                          rawAction === "VerificationRequestAssigned")
                      ? "Instructions for verifier"
                      : rawField === "ImplementationTasksSummary"
                      ? "Implementation tasks"
                    : rawField === "Description" &&
                        String(r.Action || "").trim() === "ActionItemCreate"
                      ? "Task details"
                      : rawField ||
                        (String(r.Action || "").toLowerCase().includes("create")
                          ? "Record"
                          : "—");
              const oldRaw = String(r.OldValue ?? "").trim();
              const newRaw = String(r.NewValue ?? "").trim();
              const commentsTrimmed = String(r.Comments ?? "").trim();
              const isVerificationRequestAssigned =
                rawAction === "VerificationRequestAssigned";
              const verificationAssignLegacy = isVerificationRequestAssigned
                ? isVerificationAssignedLegacyAuditRow(rawAction, rawField, newRaw)
                : false;
              /*
               * New audits: NewValue = verifier instructions (HTML). Hover shows formatted HTML;
               * anchor uses plain single line (HoverPreview anchorShowHtml={false} + capaRichHoverPreviewProps).
               * Legacy: NewValue was only the GUID — fall back to assignment summary text in Comments.
               */
              const newRawForDisplay =
                verificationAssignLegacy && commentsTrimmed
                  ? commentsTrimmed
                  : newRaw;
              const oldRawForDisplay = oldRaw;
              const statusAudit =
                rawField === "Status" || rawField.toLowerCase() === "status";
              const oldDisp = statusAudit
                ? normalizeCapaLifecycleStatusForUi(oldRawForDisplay)
                : oldRawForDisplay;
              const newDisp = statusAudit
                ? normalizeCapaLifecycleStatusForUi(newRawForDisplay)
                : newRawForDisplay;
              const hasChange = Boolean(
                String(oldDisp || "").trim() || String(newDisp || "").trim(),
              );
              const isEvidenceAudit =
                rawField === "InvestigationEvidence" ||
                rawField === "RcaEvidence";
              /* Legacy assignment rows inlined assignment summary into NEW; avoid repeating Comments. */
              const showCommentsFooter =
                Boolean(commentsTrimmed) && !verificationAssignLegacy;
              const activityNewLooksHtml =
                Boolean(String(newDisp || "").trim()) &&
                CAPA_HTML_TAG_RE.test(String(newDisp)) &&
                !verificationAssignLegacy;

              return (
                <div key={r.Id} className="capa-detail-summary__activity-item">
                  <div className="capa-detail-summary__activity-action-line">
                    {actionDisplay}
                  </div>
                  <div className="capa-detail-summary__activity-field-line">
                    <span className="capa-detail-summary__activity-field-caption">
                      Field:
                    </span>
                    <span
                      className="capa-detail-summary__activity-field-name"
                      title={fieldDisplay}
                    >
                      {fieldDisplay}
                    </span>
                  </div>
                  {hasChange ? (
                    isEvidenceAudit ? (
                      <div className="capa-detail-summary__activity-change capa-detail-summary__activity-change--inv-evidence">
                        <div className="capa-detail-summary__activity-change-row">
                          <span className="capa-detail-summary__activity-change-label">
                            Old
                          </span>
                          {oldRaw ? (
                            <InvestigationEvidenceAuditInline
                              raw={oldRaw}
                              toneClass="capa-detail-summary__activity-old"
                            />
                          ) : (
                            <span className="capa-detail-summary__activity-none">none</span>
                          )}
                        </div>
                        <div className="capa-detail-summary__activity-change-row">
                          <span className="capa-detail-summary__activity-change-label">
                            New
                          </span>
                          {newRaw ? (
                            <InvestigationEvidenceAuditInline
                              raw={newRaw}
                              toneClass="capa-detail-summary__activity-new"
                            />
                          ) : (
                            <span className="capa-detail-summary__activity-none">none</span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="capa-detail-summary__activity-change">
                        <div className="capa-detail-summary__activity-change-row">
                          <span className="capa-detail-summary__activity-change-label">
                            Old
                          </span>
                          <HoverPreview
                            {...capaRichHoverPreviewProps(oldDisp)}
                            previewLength={28}
                            anchorShowHtml={false}
                            appendEllipsis={true}
                            maxWidth={300}
                            maxHeight={180}
                            emptyPlaceholder=""
                            className="capa-detail-summary__activity-old"
                          />
                        </div>
                        <div className="capa-detail-summary__activity-change-row">
                          <span className="capa-detail-summary__activity-change-label">
                            New
                          </span>
                          <HoverPreview
                            {...capaRichHoverPreviewProps(newDisp)}
                            previewLength={activityNewLooksHtml ? 72 : 28}
                            anchorShowHtml={false}
                            appendEllipsis={true}
                            maxWidth={300}
                            maxHeight={activityNewLooksHtml ? 260 : 180}
                            emptyPlaceholder=""
                            className="capa-detail-summary__activity-new"
                          />
                        </div>
                      </div>
                    )
                  ) : null}
                  {showCommentsFooter ? (
                    <div className="capa-detail-summary__activity-comments small text-muted px-0 pt-1">
                      {commentsTrimmed}
                    </div>
                  ) : null}
                  <div className="capa-detail-summary__activity-footer">
                    <span
                      className="capa-detail-summary__activity-user-avatar capa-list-owner-avatar"
                      style={{
                        backgroundColor: getAvatarColor(actorLabel),
                      }}
                      title={actorLabel}
                    >
                      {getInitials(actorLabel)}
                    </span>
                    <span className="capa-detail-summary__activity-time">
                      {utils.formatDateTimeDisplay(r.At)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const getCapaDetailFormOptions = (
  detail,
  sourceTypeOptions = [],
  moduleOptions = [],
  sessionModules = null,
) => {
  if (!detail) return null;
  const sessionMatch =
    Array.isArray(sessionModules) && sessionModules.length > 0
      ? sessionModuleOptionForStoredCapa(detail, sessionModules)
      : null;
  const moduleOption = isCapaModuleAllValue(detail.Module)
    ? CAPA_MODULE_OPTION_ALL
    : sessionMatch ||
      moduleOptions.find((m) => m.value === detail.Module) ||
      toCapaModuleOption(detail.Module) ||
      moduleOptions[0] || { value: "", label: "—" };
  const sourceType =
    sourceTypeOptions.find((s) => s.value === detail.SourceType) ||
    sourceTypeOptions[0] || { value: "", label: detail.SourceType || "—" };
  const riskLevel =
    CAPA_RISK_OPTIONS.find((r) => r.value === detail.Risk) ||
    CAPA_RISK_OPTIONS[1];
  const entityOpt = {
    value: detail.EntityId,
    label: detail.EntityLabel || String(detail.EntityId || ""),
  };
  const tcRaw = detail.TemplateCategoryForIsf || detail.TemplateCategory;
  const tcNorm = normalizeIsfTemplateCategoryForApi(tcRaw);
  const isfCategoryOpt =
    CAPA_ISF_TEMPLATE_CATEGORIES.find(
      (c) => c.value === tcNorm || c.value === tcRaw,
    ) || CAPA_ISF_TEMPLATE_CATEGORIES[0];
  const studyIdForIsf =
    detail.IsfStudyId || detail.StudyIdForIsf || detail.StudyIdRef || "";
  const isfStudyOpt = {
    value: studyIdForIsf,
    label: detail.IsfStudyLabel || studyIdForIsf || "—",
  };
  const selectedDocumentOpt = {
    value: detail.EntityId,
    label: detail.EntityLabel || String(detail.EntityId || ""),
  };
  return {
    moduleOption,
    sourceType,
    riskLevel,
    entityOpt,
    isfStudyOpt,
    isfCategoryOpt,
    selectedDocumentOpt,
  };
};

const actionLabel = (action, currentStatus) => {
  switch (action) {
    case WORKFLOW_ACTIONS.SUBMIT_INVESTIGATION:
      return "Submit Investigation";
    case WORKFLOW_ACTIONS.SUBMIT_RCA:
      return "Submit RCA";
    case WORKFLOW_ACTIONS.SUBMIT_ACTION_PLAN:
      return "Submit for Action Plan";
    case WORKFLOW_ACTIONS.APPROVE:
      return "Approve";
    case WORKFLOW_ACTIONS.REJECT:
      return "Reject";
    case WORKFLOW_ACTIONS.MARK_COMPLETED:
      return "Mark as Completed";
    case WORKFLOW_ACTIONS.SEND_TO_VERIFICATION:
      return "Send to Verification";
    case WORKFLOW_ACTIONS.SUBMIT_VERIFICATION_EFFECTIVE:
      return "Submit Verification (Effective)";
    case WORKFLOW_ACTIONS.SUBMIT_VERIFICATION_NOT_EFFECTIVE:
      return "Submit Verification (Not Effective)";
    case WORKFLOW_ACTIONS.SEND_TO_CLOSURE:
      return "Send for closure";
    case WORKFLOW_ACTIONS.COMPLETE_CLOSURE:
      return "Closed";
    default:
      return getAdvanceButtonLabel(currentStatus);
  }
};

const CapaDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    userData,
    isSystemAdmin,
  } = useCapaAccess();
  const { showLoader, hideLoader } = useLoader();
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(true);
  const [detailLoadAttempted, setDetailLoadAttempted] = useState(false);
  const [actions, setActions] = useState([]);
  const [approverRecords, setApproverRecords] = useState([]);
  const [implementationTasks, setImplementationTasks] = useState([]);
  const [verificationRequests, setVerificationRequests] = useState([]);
  const [stageDataLoading, setStageDataLoading] = useState({});
  const [implStatusDraft, setImplStatusDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [selectedStepIndex, setSelectedStepIndex] = useState(0);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [moduleOptions, setModuleOptions] = useState([]);
  const [sourceTypeOptions, setSourceTypeOptions] = useState([]);
  const [editSourceType, setEditSourceType] = useState(null);
  const [rcaMethodOptions, setRcaMethodOptions] = useState(CAPA_RCA_METHOD_OPTIONS);
  const [editSourceRef, setEditSourceRef] = useState("");
  const [editRisk, setEditRisk] = useState(CAPA_RISK_OPTIONS[1]);
  const [staffOptions, setStaffOptions] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [investigationDetails, setInvestigationDetails] = useState("");
  const [assignedInvestigator, setAssignedInvestigator] = useState(null);
  const [evidenceFiles, setEvidenceFiles] = useState([]);
  const [evidenceUploadKey, setEvidenceUploadKey] = useState(0);
  const [pendingEvidenceFiles, setPendingEvidenceFiles] = useState([]);
  const [pendingEvidenceNotes, setPendingEvidenceNotes] = useState("");
  /** null = modal closed; 'notes' | 'full' = modal open (which fields to show) */
  const [evidenceModalMode, setEvidenceModalMode] = useState(null);
  const [rcaMethod, setRcaMethod] = useState(CAPA_RCA_METHOD_OPTIONS[0]);
  const [rcaDetails, setRcaDetails] = useState("");
  const [rcaEvidenceFiles, setRcaEvidenceFiles] = useState([]);
  const [rcaEvidenceUploadKey, setRcaEvidenceUploadKey] = useState(0);
  const [pendingRcaEvidenceFiles, setPendingRcaEvidenceFiles] = useState([]);
  const [pendingRcaEvidenceNotes, setPendingRcaEvidenceNotes] = useState("");
  /** null = modal closed; 'notes' | 'full' = RCA evidence modal open */
  const [rcaEvidenceModalMode, setRcaEvidenceModalMode] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [successMsg, setSuccessMsg] = useState("");
  const { modalState, showConfirm, handleConfirm, handleCancel } = useConfirmModal();
  const {
    alertState: taskAlertState,
    showAlert: showTaskAlert,
    hideAlert: hideTaskAlert,
  } = useInfoAlert();
  const {
    alertState: approverRecordAlertState,
    showAlert: showApproverRecordAlert,
    hideAlert: hideApproverRecordAlert,
  } = useInfoAlert();
  const {
    alertState: implTaskAlertState,
    showAlert: showImplTaskAlert,
    hideAlert: hideImplTaskAlert,
  } = useInfoAlert();
  const {
    alertState: verificationAlertState,
    showAlert: showVerificationAlert,
    hideAlert: hideVerificationAlert,
  } = useInfoAlert();
  const [newTaskDescription, setNewTaskDescription] = useState("");
  const [newTaskType, setNewTaskType] = useState("Corrective");
  const [newTaskStartDate, setNewTaskStartDate] = useState("");
  const [newTaskEndDate, setNewTaskEndDate] = useState("");
  const [newTaskApprover, setNewTaskApprover] = useState(null);
  const [actionTaskModalOpen, setActionTaskModalOpen] = useState(false);
  const [addingTask, setAddingTask] = useState(false);
  const [updatingTaskId, setUpdatingTaskId] = useState("");
  const [newApproverDescription, setNewApproverDescription] = useState("");
  const [newApproverDueDate, setNewApproverDueDate] = useState("");
  const [newApproverStaff, setNewApproverStaff] = useState([]);
  const [approvalRecordModalOpen, setApprovalRecordModalOpen] = useState(false);
  const [addingApproverRecord, setAddingApproverRecord] = useState(false);
  const [updatingApproverRecordId, setUpdatingApproverRecordId] = useState("");
  const [newImplDescription, setNewImplDescription] = useState("");
  const [newImplDueDate, setNewImplDueDate] = useState("");
  const [newImplStaff, setNewImplStaff] = useState(null);
  const [newVerReqDescription, setNewVerReqDescription] = useState("");
  const [newVerReqDueDate, setNewVerReqDueDate] = useState("");
  const [newVerReqStaff, setNewVerReqStaff] = useState(null);
  const [verifierDraft, setVerifierDraft] = useState({});
  const [verifierUploadKey, setVerifierUploadKey] = useState({});
  const [addingVerReq, setAddingVerReq] = useState(false);
  const [submittingVerificationId, setSubmittingVerificationId] = useState("");
  /** Pending row opened via Actions → Verify (assignee only). */
  const [verificationVerifyOpenId, setVerificationVerifyOpenId] = useState(null);
  const [verificationDetailOpenId, setVerificationDetailOpenId] = useState(null);
  const [closureRemarks, setClosureRemarks] = useState("");
  const [closureEvidenceFiles, setClosureEvidenceFiles] = useState([]);
  const [closureEvidenceUploadKey, setClosureEvidenceUploadKey] = useState(0);
  const [addingImplTask, setAddingImplTask] = useState(false);
  const [updatingImplTaskId, setUpdatingImplTaskId] = useState("");
  const [implTaskModalOpen, setImplTaskModalOpen] = useState(false);
  const [verificationRequestModalOpen, setVerificationRequestModalOpen] =
    useState(false);
  const detailSummaryShellRef = useRef(null);
  const actionPlanningShellRef = useRef(null);
  const approvalPlanningShellRef = useRef(null);
  const implementationShellRef = useRef(null);
  const verificationShellRef = useRef(null);
  const closureShellRef = useRef(null);
  const hydratedDetailIdRef = useRef("");
  /** Discards stale `reload()` completions (e.g. React Strict Mode double-mount). */
  const reloadGenerationRef = useRef(0);
  const loadedStageDataRef = useRef({
    actions: false,
    approvers: false,
    implementation: false,
    verification: false,
  });
  const [activityPanelHeight, setActivityPanelHeight] = useState(null);
  const capaRefId = detail?.CapaId || id;
  const capaModuleApiOpts = useMemo(
    () => capaModuleOptsFromDetail(detail, userData),
    [
      detail?.CapaId,
      detail?.OrganizationRefId,
      detail?.organizationRefId,
      userData?.OrganizationId,
    ],
  );
  const preloadedCapaRow = location?.state?.capaRow || null;

  /** Match Activity log column height to the left shell in Action Planning/Approval/Implementation/Verification/Closure.
   *  Details (step 0) renders the activity panel without a measured pixel height — flex-fill handles it. */
  useEffect(() => {
    const closureStageIdx = getStageIndex("Closure");
    const needsMeasuredActivityLog =
      selectedStepIndex === 3 ||
      selectedStepIndex === 4 ||
      selectedStepIndex === 5 ||
      selectedStepIndex === 6 ||
      selectedStepIndex === closureStageIdx;
    if (!needsMeasuredActivityLog) {
      setActivityPanelHeight(null);
      return;
    }
    const shell =
      selectedStepIndex === 3
        ? actionPlanningShellRef.current
        : selectedStepIndex === 4
          ? approvalPlanningShellRef.current
          : selectedStepIndex === 5
            ? implementationShellRef.current
            : selectedStepIndex === closureStageIdx
              ? closureShellRef.current
              : verificationShellRef.current;
    if (!shell) return;

    const syncHeight = () => {
      const h = shell.getBoundingClientRect().height;
      setActivityPanelHeight(h > 0 ? Math.ceil(h) : null);
    };

    syncHeight();
    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(syncHeight);
      observer.observe(shell);
    }
    window.addEventListener("resize", syncHeight);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener("resize", syncHeight);
    };
  }, [
    selectedStepIndex,
    detail,
    auditLog,
    actions,
    implementationTasks,
    verificationRequests,
  ]);

  const getStageDataKey = useCallback((stageIndex) => {
    const closureStageIdx = getStageIndex("Closure");
    if (stageIndex === 3) return "actions";
    if (stageIndex === 4) return "approvers";
    if (stageIndex === 5) return "implementation";
    if (stageIndex === 6 || stageIndex === closureStageIdx) return "verification";
    return null;
  }, []);

  const loadStageData = useCallback(
    async (stageIndex, opts = {}) => {
      const { force = false } = opts;
      const dataKey = getStageDataKey(stageIndex);
      const resolvedId = detail?.CapaId || id;
      if (!dataKey || !resolvedId) return;
      if (!force && loadedStageDataRef.current[dataKey]) return;

      setStageDataLoading((prev) => ({ ...prev, [stageIndex]: true }));
      try {
        if (dataKey === "actions") {
          const rows = await fetchCapaActionItems(resolvedId, userData);
          setActions(rows);
        } else if (dataKey === "approvers") {
          const rows = await fetchCapaApproverRecords(
            resolvedId,
            userData,
            capaModuleApiOpts,
          );
          setApproverRecords(rows);
        } else if (dataKey === "implementation") {
          const rows = await fetchCapaImplementationTasks(
            resolvedId,
            userData,
            capaModuleApiOpts,
          );
          setImplementationTasks(rows);
          const draft = {};
          rows.forEach((t) => {
            draft[t.ImplementationTaskId] = t.ImplementationStatus;
          });
          setImplStatusDraft(draft);
        } else if (dataKey === "verification") {
          const rows = await fetchCapaVerificationRequests(
            resolvedId,
            userData,
            capaModuleApiOpts,
          );
          setVerificationRequests(rows);
        }
        loadedStageDataRef.current[dataKey] = true;
      } finally {
        setStageDataLoading((prev) => ({ ...prev, [stageIndex]: false }));
      }
    },
    [detail?.CapaId, getStageDataKey, id, userData, capaModuleApiOpts],
  );

  const reload = useCallback(async () => {
    const gen = ++reloadGenerationRef.current;
    setDetailLoading(true);
    setDetailLoadAttempted(false);
    showLoader("Loading CAPA details...");
    try {
      const { detail: d, auditLogFromGetById, actionItemsFromGetById } =
        await fetchCapaById(id, userData);
      if (gen !== reloadGenerationRef.current) {
        return d;
      }
      setDetail(d ?? null);
      if (auditLogFromGetById !== null) {
        setAuditLog(auditLogFromGetById);
      } else {
        const rows = await fetchCapaAuditLogForCapa(d?.CapaId || id, userData);
        if (gen !== reloadGenerationRef.current) {
          return d;
        }
        setAuditLog(rows);
      }
      /* Action items ship on GetById (Activity proc); Module GetActionItems can fail separately — hydrate here. */
      if (actionItemsFromGetById !== null) {
        setActions(actionItemsFromGetById);
        loadedStageDataRef.current.actions = true;
      } else {
        loadedStageDataRef.current.actions = false;
      }
      /* Approver rows are not on GetById — always load once after detail is known. */
      const approverRows = await fetchCapaApproverRecords(
        d?.CapaId || id,
        userData,
        capaModuleOptsFromDetail(d, userData),
      );
      if (gen !== reloadGenerationRef.current) {
        return d;
      }
      setApproverRecords(approverRows);
      loadedStageDataRef.current.approvers = true;
      return d;
    } finally {
      hideLoader();
      if (gen === reloadGenerationRef.current) {
        setDetailLoadAttempted(true);
        setDetailLoading(false);
      }
    }
  }, [hideLoader, id, showLoader, userData]);

  useEffect(() => {
    if (!userData) return;
    const normalizedId = String(id || "").trim().toLowerCase();
    if (!normalizedId) return;
    if (hydratedDetailIdRef.current === normalizedId) return;
    hydratedDetailIdRef.current = normalizedId;

    loadedStageDataRef.current = {
      actions: false,
      approvers: false,
      implementation: false,
      verification: false,
    };
    setStageDataLoading({});
    setActions([]);
    setApproverRecords([]);
    setImplementationTasks([]);
    setVerificationRequests([]);
    setImplStatusDraft({});
    if (
      preloadedCapaRow &&
      String(preloadedCapaRow?.CapaId ?? preloadedCapaRow?.capaId ?? "")
        .trim()
        .toLowerCase() === String(id || "").trim().toLowerCase()
    ) {
      setDetail((prev) => prev || preloadedCapaRow);
      setDetailLoading(false);
      setDetailLoadAttempted(true);
    }
    reload();
  }, [id, preloadedCapaRow?.CapaId, reload, userData]);

  const loadAuditLog = useCallback(async () => {
    const rows = await fetchCapaAuditLogForCapa(
      capaRefId,
      userData,
      capaModuleApiOpts,
    );
    setAuditLog(rows);
  }, [capaRefId, userData, capaModuleApiOpts]);

  /* Keep URL aligned with server CapaId so saves/audit always target the same row (e.g. /capa/{CapaNo} → /capa/{guid}). */
  useEffect(() => {
    const canonical = String(detail?.CapaId || "").trim();
    const route = String(id || "").trim();
    if (!canonical || !route) return;
    if (normalizeCapaRouteToken(canonical) === normalizeCapaRouteToken(route)) return;
    if (!isCapaGuidToken(canonical)) return;
    if (
      !isCapaGuidToken(route) ||
      normalizeCapaRouteToken(route) !== normalizeCapaRouteToken(canonical)
    ) {
      navigate(`/capa/${canonical}`, { replace: true, state: location.state });
    }
  }, [detail?.CapaId, id, navigate, location.state]);

  useEffect(() => {
    if (!verificationVerifyOpenId) return;
    const r = verificationRequests.find(
      (x) => x.VerificationRequestId === verificationVerifyOpenId,
    );
    if (
      !r ||
      r.RequestStatus !== "Pending" ||
      !verificationRequestAssigneeMatches(r, userData)
    ) {
      setVerificationVerifyOpenId(null);
    }
  }, [verificationRequests, verificationVerifyOpenId, userData]);

  useEffect(() => {
    if (!detail?.CapaId) return;
    setSelectedStepIndex(getStageIndex(detail.Status));
  }, [detail?.CapaId]);

  useEffect(() => {
    loadStageData(selectedStepIndex);
  }, [selectedStepIndex, loadStageData]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!userData?.OrganizationId) return;
      try {
        const cfg = await fetchCapaConfig(userData);
        const moduleFromApi = Array.isArray(cfg?.modules) ? cfg.modules : [];
        const mappedModules = capaModuleDropdownOptionsWithAll(
          resolveCapaModuleDropdownOptions(userData, moduleFromApi),
        );
        const fromApi = Array.isArray(cfg?.sourceTypes) ? cfg.sourceTypes : [];
        const mapped = fromApi
          .map((s) => String(s || "").trim())
          .filter(Boolean)
          .map((v) => ({ value: v, label: v }));
        const rcaFromApi = Array.isArray(cfg?.rcaMethods) ? cfg.rcaMethods : [];
        const mappedRca = rcaFromApi
          .map((s) => String(s || "").trim())
          .filter(Boolean)
          .map((v) => ({ value: v, label: v }));
        if (!cancelled) {
          setModuleOptions(mappedModules);
        }
        if (!cancelled && mapped.length > 0) {
          setSourceTypeOptions(mapped);
        }
        if (!cancelled && mappedRca.length > 0) {
          setRcaMethodOptions(mappedRca);
        }
      } catch {
        /* fallback stays static */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userData?.OrganizationId, userData?.StaffId, userData?.Modules]);

  useEffect(() => {
    if (!detail) return;
    setEditTitle(detail.Title || "");
    setEditDescription(detail.Description || "");
    setEditSourceType(optionFromValue(sourceTypeOptions, detail.SourceType));
    setEditSourceRef(detail.SourceRef || "");
    setEditRisk(optionFromValue(CAPA_RISK_OPTIONS, detail.Risk));
    setInvestigationDetails(detail.InvestigationSummary || "");
    setEvidenceFiles(parseEvidenceFiles(detail.SupportingEvidenceFiles));
    setPendingEvidenceFiles([]);
    setPendingEvidenceNotes("");
    setEvidenceUploadKey((k) => k + 1);
    setRcaMethod(rcaMethodOptionFromStored(detail.RcaMethod, rcaMethodOptions));
    setRcaDetails(detail.RootCauseSummary || "");
    setRcaEvidenceFiles(parseEvidenceFiles(detail.RcaEvidenceFiles));
    setPendingRcaEvidenceFiles([]);
    setPendingRcaEvidenceNotes("");
    setRcaEvidenceUploadKey((k) => k + 1);
    if (detail.AssignedInvestigatorId || detail.AssignedInvestigatorName) {
      setAssignedInvestigator({
        value: detail.AssignedInvestigatorId || detail.OwnerStaffId || "",
        label:
          detail.AssignedInvestigatorName || detail.OwnerName || "Investigator",
      });
    } else if (detail.OwnerStaffId || detail.OwnerName) {
      setAssignedInvestigator({
        value: detail.OwnerStaffId || "",
        label: detail.OwnerName || "Investigator",
      });
    } else {
      setAssignedInvestigator(null);
    }
    setClosureRemarks(detail.ClosureRemarks || "");
    setClosureEvidenceFiles(parseEvidenceFiles(detail.ClosureEvidenceFiles));
    setClosureEvidenceUploadKey((k) => k + 1);
  }, [detail, sourceTypeOptions, rcaMethodOptions]);

  const loadStaff = useCallback(async () => {
    if (!userData?.OrganizationId || !userData?.StaffId) return;
    setLoadingStaff(true);
    try {
      const response = await api.post(API_ENDPOINTS.USER.GET_USERLIST, {
        OrganizationId: userData.OrganizationId,
        StaffId: userData.StaffId,
        Action: "Get",
        AccessCode: "",
        Password: "",
        HaveProfileViewRights: 1,
        HaveProfileEditRights: 0,
        HaveDelegationLogViewRights: 1,
        HaveDelegationLogEditRights: 0,
        HavingLineManagerRights: 1,
        HaveStudyTaskArchiveRights: 0,
        CreatedBy: userData.StaffId,
      });
      if (response?.success === 1) {
        const list = response.data?.UsersActivity || [];
        setStaffOptions(mapCapaStaffOptions(list));
      }
    } finally {
      setLoadingStaff(false);
    }
  }, [userData]);

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  const investigationComplete = useMemo(() => {
    const detailsOk = Boolean(stripHtml(String(investigationDetails || "")).trim());
    const investigatorOk = Boolean(assignedInvestigator?.value);
    return detailsOk && investigatorOk;
  }, [investigationDetails, assignedInvestigator]);

  const canEditThisCapa = useMemo(
    () =>
      canUpdateCapaRecord({
        capaRow: detail,
        userData,
        isSystemAdmin,
      }),
    [detail, userData, isSystemAdmin],
  );

  const canManageCapaStage = useMemo(
    () =>
      canChangeCapaStage({
        capaRow: detail,
        userData,
        isSystemAdmin,
      }),
    [detail, userData, isSystemAdmin],
  );

  const canEditInvestigation = useMemo(
    () => Boolean(canManageCapaStage && detail?.Status === "Under Investigation"),
    [canManageCapaStage, detail?.Status],
  );

  const canEditRca = useMemo(
    () => Boolean(canManageCapaStage && detail?.Status === "Under RCA"),
    [canManageCapaStage, detail?.Status],
  );

  const rcaComplete = useMemo(() => {
    const methodOk = Boolean(rcaMethod?.value);
    const detailsOk = Boolean(stripHtml(String(rcaDetails || "")).trim());
    return methodOk && detailsOk;
  }, [rcaMethod, rcaDetails]);

  const rcaDirty = useMemo(() => {
    if (!detail) return false;
    const savedMethod = rcaMethodOptionFromStored(detail.RcaMethod || "", rcaMethodOptions);
    const methodChanged =
      (rcaMethod?.value || "") !== (savedMethod.value || "");
    const detailsChanged =
      stripHtml(rcaDetails || "") !==
      stripHtml(detail.RootCauseSummary || "");
    const filesChanged =
      normalizeEvidenceFilesForCompare(rcaEvidenceFiles) !==
      normalizeEvidenceFilesForCompare(parseEvidenceFiles(detail.RcaEvidenceFiles));
    return methodChanged || detailsChanged || filesChanged;
  }, [detail, rcaMethod, rcaDetails, rcaEvidenceFiles, rcaMethodOptions]);

  const canEditActionPlanning = useMemo(
    () => Boolean(canManageCapaStage && detail?.Status === "Action Planning"),
    [canManageCapaStage, detail?.Status],
  );

  const canEditApprovalPlan = useMemo(
    () => Boolean(canManageCapaStage && detail?.Status === "Approved"),
    [canManageCapaStage, detail?.Status],
  );

  const canEditImplementation = useMemo(
    () => Boolean(canManageCapaStage && isCapaImplementationPhase(detail?.Status)),
    [canManageCapaStage, detail?.Status],
  );

  const canEditVerification = useMemo(
    () => Boolean(canManageCapaStage && detail?.Status === "Verification"),
    [canManageCapaStage, detail?.Status],
  );

  const handledStaffOptions = useMemo(
    () =>
      staffOptions.map((opt) => ({
        value: opt.value,
        label: opt.label,
        emailId: opt.emailId || "",
      })),
    [staffOptions],
  );

  /** Options for shared `Dropdown` (search + optional multi on component; CAPA uses single select). */
  const staffDropdownOptions = useMemo(
    () => [
      { value: "", label: "Select staff" },
      ...handledStaffOptions,
    ],
    [handledStaffOptions],
  );

  const evidenceTableColumns = useMemo(
    () => [
      { label: "File", accessor: "fileName" },
      { label: "Size", accessor: "fileSize" },
      { label: "Notes", accessor: "notes" },
    ],
    [],
  );

  const evidenceTableRows = useMemo(
    () => {
      const toEpoch = (v) => {
        const t = Date.parse(String(v || ""));
        return Number.isNaN(t) ? 0 : t;
      };
      return [...evidenceFiles]
        .sort(
          (a, b) =>
            toEpoch(b.At || b.CreatedAt || b.createdAt) -
            toEpoch(a.At || a.CreatedAt || a.createdAt),
        )
        .map((f) => ({
        id: f.id,
        fileName: f.name || "—",
        fileSize: f.size != null ? formatFileSize(f.size) : "—",
        notes: f.notes?.trim() || "—",
        addedAtLabel:
          f.At || f.CreatedAt || f.createdAt
            ? utils.formatDateTimeDisplay(f.At || f.CreatedAt || f.createdAt)
            : "—",
        downloadName: f.name || "evidence-file",
        dataUrl: f.dataUrl || "",
      }));
    },
    [evidenceFiles],
  );


  const rcaEvidenceTableRows = useMemo(
    () =>
      rcaEvidenceFiles.map((f) => ({
        id: f.id,
        fileName: f.name || "—",
        fileSize: f.size != null ? formatFileSize(f.size) : "—",
        notes: f.notes?.trim() || "—",
        addedAtLabel:
          f.At || f.CreatedAt || f.createdAt
            ? utils.formatDateTimeDisplay(f.At || f.CreatedAt || f.createdAt)
            : "—",
        downloadName: f.name || "evidence-file",
        dataUrl: f.dataUrl || "",
      })),
    [rcaEvidenceFiles],
  );

  const closureEvidenceTableRows = useMemo(
    () =>
      closureEvidenceFiles.map((f) => ({
        id: f.id,
        fileName: f.name || "—",
        fileSize: f.size != null ? formatFileSize(f.size) : "—",
        notes: f.notes?.trim() || "—",
        downloadName: f.name || "closure-evidence",
        dataUrl: f.dataUrl || "",
      })),
    [closureEvidenceFiles],
  );

  /** After CAPA is Closed — attachments shown in DataTable (view only). */
  const closureEvidenceReadOnlyTableActions = useMemo(
    () => [
      {
        label: "View file",
        className: "btn-outline-primary",
        visible: (row) => Boolean(row.dataUrl),
        onClick: (row) => {
          window.open(row.dataUrl, "_blank", "noopener,noreferrer");
        },
      },
    ],
    [],
  );

  const actionItemColumns = useMemo(
    () => [
      {
        label: "Task details",
        accessor: "Description",
        render: (row) => (
          <HoverPreview
            {...capaRichHoverPreviewProps(row.Description)}
            previewLength={28}
            anchorShowHtml={false}
          />
        ),
      },
      { label: "Task type", accessor: "Type" },
      {
        label: "Start date",
        accessor: "StartDate",
        render: (row) => formatVerificationDateOnly(row.StartDate),
      },
      {
        label: "End date",
        accessor: "DueDate",
        render: (row) => formatVerificationDateOnly(row.DueDate),
      },
      {
        label: "Assigned Staff",
        accessor: "AssignedStaffLabel",
        align: "center",
        render: (row) => {
          const fullName = row.AssignedStaffLabel || "—";
          return (
            <div className="capa-list-owner-only">
              <div
                className="capa-list-owner-avatar"
                style={{ backgroundColor: getAvatarColor(fullName) }}
                title={fullName}
              >
                {getInitials(fullName)}
              </div>
            </div>
          );
        },
      },
      { label: "Status", accessor: "StatusLabel" },
      {
        label: "Reason",
        accessor: "DecisionReasonLabel",
        render: (row) => (
          <HoverPreview
            {...capaRichHoverPreviewProps(row.DecisionReasonLabel)}
            previewLength={24}
            anchorShowHtml={false}
          />
        ),
      },
    ],
    [],
  );

  const actionItemRows = useMemo(
    () =>
      actions.map((r) => {
        const ownerStaffId = String(r.OwnerStaffId || "").toLowerCase();
        const currentStaffId = String(userData?.StaffId || "").toLowerCase();
        const canReview = Boolean(
          currentStaffId &&
            ownerStaffId &&
            (ownerStaffId === currentStaffId || ownerStaffId === "__current_user__"),
        );
        const statusLabel = getActionItemStatusLabel(r.Status);
        return {
          ...r,
          StartDate: r.StartDate || r.DueDate,
          AssignedStaffLabel: r.OwnerName || "—",
          StatusLabel: statusLabel,
          DecisionReasonLabel: (r.DecisionReason || "").trim() || "—",
          CanReview: canReview,
          CanApprove: canReview && statusLabel === "N/A",
          CanReject: canReview && statusLabel === "N/A",
        };
      }),
    [actions, userData?.StaffId],
  );

  const approverRecordColumns = useMemo(
    () => [
      {
        label: "Description",
        accessor: "Description",
        render: (row) => (
          <HoverPreview
            {...capaRichHoverPreviewProps(row.Description)}
            previewLength={28}
            anchorShowHtml={false}
          />
        ),
      },
      { label: "Due date", accessor: "DueDate", type: "date" },
      {
        label: "Approver",
        accessor: "ApproverLabel",
        align: "center",
        render: (row) => {
          const fullName = row.ApproverLabel || "—";
          return (
            <div className="capa-list-owner-only">
              <div
                className="capa-list-owner-avatar"
                style={{ backgroundColor: getAvatarColor(fullName) }}
                title={fullName}
              >
                {getInitials(fullName)}
              </div>
            </div>
          );
        },
      },
      { label: "Status", accessor: "StatusLabel" },
      {
        label: "Reason",
        accessor: "DecisionReasonLabel",
        render: (row) => (
          <HoverPreview
            {...capaRichHoverPreviewProps(row.DecisionReasonLabel)}
            previewLength={24}
            anchorShowHtml={false}
          />
        ),
      },
    ],
    [],
  );

  const approverRecordRows = useMemo(
    () =>
      approverRecords.map((r) => {
        const sid = String(r.ApproverStaffId || "").toLowerCase();
        const currentStaffId = String(userData?.StaffId || "").toLowerCase();
        const canReview = Boolean(
          currentStaffId &&
            sid &&
            (sid === currentStaffId || sid === "__current_user__"),
        );
        const statusLabel = getActionItemStatusLabel(r.Status);
        return {
          ...r,
          ApproverLabel: r.ApproverName || "—",
          StatusLabel: statusLabel,
          DecisionReasonLabel: (r.DecisionReason || "").trim() || "—",
          CanApprove: canReview && statusLabel === "N/A",
          CanReject: canReview && statusLabel === "N/A",
        };
      }),
    [approverRecords, userData?.StaffId],
  );

  const implementationTaskRows = useMemo(
    () =>
      implementationTasks.map((t) => {
        const sid = String(t.AssignedStaffId || "").toLowerCase();
        const currentStaffId = String(userData?.StaffId || "").toLowerCase();
        const isAssignee = Boolean(
          currentStaffId &&
            sid &&
            (sid === currentStaffId || sid === "__current_user__"),
        );
        const statusSaved = Boolean(
          t.StatusUpdatedAt != null && String(t.StatusUpdatedAt).trim() !== "",
        );
        const persistedStatus = String(t.ImplementationStatus || "").trim();
        const isCompletedInDb = persistedStatus === "Completed";
        return {
          ...t,
          AssignedStaffLabel: t.AssignedName || "—",
          LastCommentLabel: (t.StatusChangeDescription || "").trim() || "—",
          CanUpdateStatus: isAssignee,
          /** Saved as Completed: no further status edits; dropdown shown disabled for assignee. */
          ImplementationStatusCompletedLocked: isCompletedInDb,
          /** Remove only before any implementation status has been saved (auth flow). */
          CanRemove: !statusSaved,
        };
      }),
    [implementationTasks, userData?.StaffId],
  );

  const implementationTaskColumns = useMemo(
    () => [
      {
        label: "Details",
        accessor: "Description",
        render: (row) => (
          <HoverPreview
            {...capaRichHoverPreviewProps(row.Description)}
            previewLength={28}
            anchorShowHtml={false}
          />
        ),
      },
      { label: "Due date", accessor: "DueDate", type: "date" },
      {
        label: "Staff",
        accessor: "AssignedStaffLabel",
        align: "center",
        render: (row) => {
          const fullName = row.AssignedStaffLabel || "—";
          return (
            <div className="capa-list-owner-only">
              <div
                className="capa-list-owner-avatar"
                style={{ backgroundColor: getAvatarColor(fullName) }}
                title={fullName}
              >
                {getInitials(fullName)}
              </div>
            </div>
          );
        },
      },
      {
        label: "Implementation status",
        accessor: "ImplementationStatus",
        render: (row) =>
          row.CanUpdateStatus ? (
            <div className="capa-impl-status-dd" onClick={(e) => e.stopPropagation()}>
              <Dropdown
                options={CAPA_IMPLEMENTATION_STATUS_OPTIONS}
                selected={implStatusOptionFromValue(
                  row.ImplementationStatusCompletedLocked
                    ? row.ImplementationStatus
                    : (implStatusDraft[row.ImplementationTaskId] ??
                        row.ImplementationStatus),
                )}
                onSelect={(opt) =>
                  setImplStatusDraft((d) => ({
                    ...d,
                    [row.ImplementationTaskId]: opt.value,
                  }))
                }
                disabled={
                  updatingImplTaskId === row.ImplementationTaskId ||
                  row.ImplementationStatusCompletedLocked
                }
                showSearch
              />
            </div>
          ) : (
            row.ImplementationStatus || "—"
          ),
      },
      {
        label: "Additional comments",
        accessor: "LastCommentLabel",
        render: (row) => (
          <HoverPreview
            {...capaRichHoverPreviewProps(row.LastCommentLabel)}
            previewLength={24}
            anchorShowHtml={false}
          />
        ),
      },
    ],
    [implStatusDraft, updatingImplTaskId],
  );

  const verificationRows = useMemo(
    () =>
      verificationRequests.map((r) => ({
        ...r,
        StatusLabel:
          r.RequestStatus === "Submitted" && r.Outcome === "Effective"
            ? "Effective"
            : r.RequestStatus === "Submitted" && r.Outcome === "Not Effective"
              ? "Not Effective"
              : "Open",
        OutcomeLabel: r.Outcome || "—",
        AssignedStaffLabel: r.AssignedName || "—",
        RequestedByLabel: r.RequestedByName || "—",
        CanVerify:
          r.RequestStatus === "Pending" &&
          verificationRequestAssigneeMatches(r, userData),
      })),
    [verificationRequests, userData],
  );

  const allTasksReviewed = useMemo(() => {
    if (!actionItemRows.length) return false;
    return actionItemRows.every((r) => {
      const s = getActionItemStatusLabel(r.Status);
      return s === "Accepted" || s === "Rejected";
    });
  }, [actionItemRows]);

  const pendingTaskDecisionCount = useMemo(
    () =>
      actionItemRows.filter(
        (r) => getActionItemStatusLabel(r.Status) === "N/A",
      ).length,
    [actionItemRows],
  );

  const allApproverRecordsReviewed = useMemo(() => {
    if (!approverRecords.length) return false;
    return approverRecords.every((r) => {
      const s = getActionItemStatusLabel(r.Status);
      return s === "Accepted" || s === "Rejected";
    });
  }, [approverRecords]);

  const pendingApproverDecisionCount = useMemo(
    () =>
      approverRecords.filter(
        (r) => getActionItemStatusLabel(r.Status) === "N/A",
      ).length,
    [approverRecords],
  );

  const allImplementationTasksReadyForVerification = useMemo(() => {
    if (!implementationTasks.length) return false;
    return implementationTasks.every((t) => {
      const s = String(t.ImplementationStatus || "").trim();
      return s !== "" && !CAPA_IMPLEMENTATION_BLOCKING_FOR_VERIFICATION.has(s);
    });
  }, [implementationTasks]);

  const pendingImplementationNotStartedCount = useMemo(
    () =>
      implementationTasks.filter(
        (t) => String(t.ImplementationStatus || "").trim() === "Not Started",
      ).length,
    [implementationTasks],
  );

  const pendingImplementationInProgressCount = useMemo(
    () =>
      implementationTasks.filter(
        (t) => String(t.ImplementationStatus || "").trim() === "In Progress",
      ).length,
    [implementationTasks],
  );

  const todayDate = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const resetNewTaskForm = () => {
    setNewTaskDescription("");
    setNewTaskType("Corrective");
    setNewTaskStartDate("");
    setNewTaskEndDate("");
    setNewTaskApprover(null);
  };

  const handleAddActionItem = useCallback(async () => {
    if (!canEditActionPlanning) return;
    const descHtml = String(newTaskDescription || "").trim();
    const descPlain = stripHtml(descHtml).trim();
    if (!descPlain) {
      showTaskAlert({
        title: "Action planning",
        message: "Enter task details before adding.",
        type: "warning",
      });
      return;
    }
    if (!newTaskStartDate) {
      showTaskAlert({
        title: "Action planning",
        message: "Select a start date for the task.",
        type: "warning",
      });
      return;
    }
    if (!newTaskApprover?.value) {
      showTaskAlert({
        title: "Action planning",
        message: "Select a staff member who will approve/reject the task.",
        type: "warning",
      });
      return;
    }
    if (!newTaskEndDate) {
      showTaskAlert({
        title: "Action planning",
        message: "Select an end date for the task.",
        type: "warning",
      });
      return;
    }
    setAddingTask(true);
    try {
      const payload = {
        Type: newTaskType,
        Description: descHtml,
        OwnerName: newTaskApprover.label,
        OwnerStaffId: newTaskApprover.value,
        StartDate: newTaskStartDate || null,
        DueDate: newTaskEndDate,
        Status: "N/A",
      };
      const updated = await createCapaActionItem(detail.CapaId, payload, userData);
      setActions(updated);
      resetNewTaskForm();
      setActionTaskModalOpen(false);
    } catch (e) {
      setErr(e?.message || "Failed to add task.");
    } finally {
      setAddingTask(false);
    }
  }, [
    canEditActionPlanning,
    newTaskDescription,
    newTaskApprover,
    newTaskEndDate,
    newTaskType,
    newTaskStartDate,
    detail?.CapaId,
    userData,
  ]);

  const handleTaskDecision = useCallback(
    async (actionItemId, nextStatus) => {
      if (!detail?.CapaId || !actionItemId) return;
      const authRes = await openAuthModal({
        showDescription: true,
        requireDescription: true,
      });
      if (authRes?.Success !== 1) {
        if (!authRes?.cancelled) {
          showTaskAlert({
            title: "Action planning",
            message: "Two-factor authentication failed or cancelled.",
            type: "warning",
          });
        }
        return;
      }
      const reason = String(authRes?.Description || "").trim();
      if (!reason) {
        showTaskAlert({
          title: "Action planning",
          message: "Reason is required in auth modal.",
          type: "warning",
        });
        return;
      }
      setUpdatingTaskId(actionItemId);
      try {
        const updated = await updateCapaActionItemStatus(
          detail.CapaId,
          actionItemId,
          nextStatus,
          reason,
          userData,
        );
        setActions(updated);
        await loadAuditLog();
      } catch (e) {
        showTaskAlert({
          title: "Action planning",
          message: e?.message || "Unable to update task status.",
          type: "warning",
        });
      } finally {
        setUpdatingTaskId("");
      }
    },
    [detail?.CapaId, showTaskAlert, userData, loadAuditLog],
  );

  const draftDetailsDirty = useMemo(() => {
    if (!detail) return false;
    const titleChanged =
      (editTitle || "").trim() !== (detail.Title || "").trim();
    const descChanged =
      stripHtml(editDescription || "") !== stripHtml(detail.Description || "");
    const sourceTypeChanged =
      (editSourceType?.value || "") !== (detail.SourceType || "");
    const sourceRefChanged =
      (editSourceRef || "").trim() !== (detail.SourceRef || "").trim();
    const riskChanged = (editRisk?.value || "") !== (detail.Risk || "");
    return (
      titleChanged ||
      descChanged ||
      sourceTypeChanged ||
      sourceRefChanged ||
      riskChanged
    );
  }, [detail, editTitle, editDescription, editSourceType, editSourceRef, editRisk]);

  const investigationDirty = useMemo(() => {
    if (!detail) return false;
    const summaryChanged =
      stripHtml(investigationDetails || "") !==
      stripHtml(detail.InvestigationSummary || "");
    const savedInvestigatorId =
      detail.AssignedInvestigatorId || detail.OwnerStaffId || "";
    const investigatorChanged =
      (assignedInvestigator?.value || "") !== (savedInvestigatorId || "");
    const filesChanged =
      normalizeEvidenceFilesForCompare(evidenceFiles) !==
      normalizeEvidenceFilesForCompare(parseEvidenceFiles(detail.SupportingEvidenceFiles));
    return summaryChanged || investigatorChanged || filesChanged;
  }, [
    detail,
    investigationDetails,
    assignedInvestigator,
    evidenceFiles,
  ]);

  const anyCapaDirty = useMemo(
    () => draftDetailsDirty || investigationDirty || rcaDirty,
    [draftDetailsDirty, investigationDirty, rcaDirty],
  );

  const workflowActions = useMemo(() => {
    if (!detail || !userData) return [];
    const roles = getCapaRoles(detail, userData.StaffId);
    return getAvailableWorkflowActions({
      status: detail.Status,
      roles,
      isSystemAdmin,
    });
  }, [detail, userData, isSystemAdmin]);

  const verificationReadyForClosure = useMemo(() => {
    if (!verificationRequests.length) return true;
    const pending = verificationRequests.some(
      (r) => r.RequestStatus === "Pending",
    );
    if (pending) return false;
    return verificationRequests.some((r) => r.Outcome === "Effective");
  }, [verificationRequests]);

  const canSendForClosure = useMemo(
    () =>
      Boolean(
        canEditVerification &&
          workflowActions.includes(WORKFLOW_ACTIONS.SEND_TO_CLOSURE) &&
          verificationReadyForClosure,
      ),
    [canEditVerification, workflowActions, verificationReadyForClosure],
  );

  const canEditClosure = useMemo(
    () => Boolean(canManageCapaStage && detail?.Status === "Closure"),
    [canManageCapaStage, detail?.Status],
  );

  const canCompleteClosure = useMemo(
    () =>
      Boolean(
        canEditClosure &&
          workflowActions.includes(WORKFLOW_ACTIONS.COMPLETE_CLOSURE),
      ),
    [canEditClosure, workflowActions],
  );

  const detailFormOpts = useMemo(
    () =>
      detail
        ? getCapaDetailFormOptions(
            detail,
            sourceTypeOptions,
            moduleOptions,
            Array.isArray(userData?.Modules) ? userData.Modules : null,
          )
        : null,
    [detail, sourceTypeOptions, moduleOptions, userData?.Modules],
  );

  const handleStepperStepClick = useCallback((stepIndex) => {
    setSelectedStepIndex(stepIndex);
    document
      .getElementById("capa-detail-step-panel")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const selectedStageDataLoading = useMemo(
    () => Boolean(stageDataLoading[selectedStepIndex]),
    [selectedStepIndex, stageDataLoading],
  );

  const saveDetailFieldsOnly = useCallback(async () => {
    const base = {
      CapaId: capaRefId,
      Title: editTitle.trim(),
      Description: editDescription ?? "",
      sourceType: editSourceType?.value,
      sourceRef: editSourceRef.trim(),
      risk: editRisk?.value,
      investigationSummary: (investigationDetails || "").trim(),
      assignedInvestigatorId: assignedInvestigator?.value || "",
      assignedInvestigatorName: assignedInvestigator?.label || "",
      ownerStaffId: assignedInvestigator?.value || detail?.OwnerStaffId || "",
      ownerName: assignedInvestigator?.label || detail?.OwnerName || "",
      supportingEvidence: "",
      supportingEvidenceFiles: JSON.stringify(evidenceFiles),
    };
    const withRca = {
      ...base,
      rootCauseSummary: (rcaDetails || "").trim(),
      rcaMethod: rcaMethod?.value || "",
      rcaEvidenceFiles: JSON.stringify(rcaEvidenceFiles),
    };
    await saveCapa(
      rcaDirty ? withRca : omitRcaAndClosureFromCapaPayload(withRca),
      userData,
    );
  }, [
    capaRefId,
    userData,
    editTitle,
    editDescription,
    editSourceType,
    editSourceRef,
    editRisk,
    investigationDetails,
    assignedInvestigator,
    evidenceFiles,
    rcaDetails,
    rcaMethod,
    rcaEvidenceFiles,
    rcaDirty,
    detail?.OwnerStaffId,
    detail?.OwnerName,
  ]);

  const handleEvidenceFileUploadSelect = useCallback(
    (data) => {
      if (!canEditInvestigation) return;
      const batch = Array.isArray(data) ? data : data ? [data] : [];
      if (!batch.length) return;
      setErr(null);
      setPendingEvidenceFiles(batch);
    },
    [canEditInvestigation],
  );

  const handleEvidenceUploadError = useCallback((error) => {
    const msg =
      error?.message ||
      (typeof error === "string" ? error : "File upload error");
    setErr(msg);
  }, []);

  const doRemoveEvidenceFile = useCallback(
    async (fileId) => {
      if (!canEditInvestigation) return;
      if (!String(capaRefId || "").trim()) {
        setErr("Cannot save: CAPA id is missing. Refresh the page and try again.");
        return;
      }
      const nextEvidence = evidenceFiles.filter((f) => f.id !== fileId);
      setErr(null);
      setEvidenceFiles(nextEvidence);
      setBusy(true);
      try {
        await saveCapa(
          omitRcaAndClosureFromCapaPayload({
            CapaId: capaRefId,
            Title: editTitle.trim(),
            Description: editDescription ?? "",
            sourceType: editSourceType?.value,
            sourceRef: editSourceRef.trim(),
            risk: editRisk?.value,
            investigationSummary: (investigationDetails || "").trim(),
            assignedInvestigatorId: assignedInvestigator?.value || "",
            assignedInvestigatorName: assignedInvestigator?.label || "",
            ownerStaffId: assignedInvestigator?.value || detail?.OwnerStaffId || "",
            ownerName: assignedInvestigator?.label || detail?.OwnerName || "",
            supportingEvidence: "",
            supportingEvidenceFiles: JSON.stringify(nextEvidence),
            rootCauseSummary: (rcaDetails || "").trim(),
            rcaMethod: rcaMethod?.value || "",
            rcaEvidenceFiles: JSON.stringify(rcaEvidenceFiles),
          }),
          userData,
        );
        await reload();
        await loadAuditLog();
      } catch (e) {
        setErr(e?.message || "Save failed");
        await reload();
        await loadAuditLog();
      } finally {
        setBusy(false);
      }
    },
    [
      canEditInvestigation,
      evidenceFiles,
      capaRefId,
      userData,
      editTitle,
      editDescription,
      editSourceType,
      editSourceRef,
      editRisk,
      investigationDetails,
      assignedInvestigator,
      detail?.OwnerStaffId,
      detail?.OwnerName,
      reload,
      loadAuditLog,
    ],
  );

  const confirmRemoveEvidenceFile = useCallback(
    (fileId) => {
      showConfirm({
        title: "Remove evidence",
        message: "Are you sure you want to delete this row?",
        confirmText: "Yes, delete",
        cancelText: "Cancel",
        confirmButtonClass: "btn-danger",
        icon: "fas fa-exclamation-circle text-warning",
        onConfirm: () => {
          void doRemoveEvidenceFile(fileId);
        },
      });
    },
    [showConfirm, doRemoveEvidenceFile],
  );

  const handleAddEvidenceRow = useCallback(async () => {
    if (!canEditInvestigation) {
      setErr(
        "Investigation evidence cannot be saved in the current CAPA status. Open this CAPA while it is Under Investigation.",
      );
      return;
    }
    if (!String(capaRefId || "").trim()) {
      setErr("Cannot save evidence: CAPA id is missing. Refresh the page and try again.");
      return;
    }
    const note = String(pendingEvidenceNotes || "").trim();
    const hasNote = Boolean(note);
    const hasFile = pendingEvidenceFiles.length > 0;
    if (!hasNote && !hasFile) {
      setErr("Add a file or notes before clicking Add.");
      return;
    }
    const remaining = MAX_EVIDENCE_FILES - evidenceFiles.length;
    if (remaining <= 0) {
      setErr(`Maximum ${MAX_EVIDENCE_FILES} evidence rows`);
      return;
    }
    let additions = [];
    if (hasFile) {
      additions = pendingEvidenceFiles.map((f) => ({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
        createdAt: new Date().toISOString(),
        name: f.name,
        size: f.size,
        type: f.type || "",
        dataUrl: `data:${f.type || "application/octet-stream"};base64,${f.data}`,
        notes: note,
      }));
    } else {
      additions = [
        {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
          createdAt: new Date().toISOString(),
          name: "",
          size: null,
          type: "",
          dataUrl: "",
          notes: note,
        },
      ];
    }
    if (additions.length > remaining) {
      setErr(
        `Too many files for the remaining slots (max ${MAX_EVIDENCE_FILES} rows, ${remaining} left). Remove a row or add fewer files.`,
      );
      return;
    }
    const slice = additions;
    const nextEvidence = [...evidenceFiles, ...slice];
    setErr(null);
    setEvidenceFiles(nextEvidence);
    setPendingEvidenceFiles([]);
    setPendingEvidenceNotes("");
    setEvidenceUploadKey((k) => k + 1);
    setEvidenceModalMode(null);
    setBusy(true);
    try {
      await saveCapa(
        omitRcaAndClosureFromCapaPayload({
          CapaId: capaRefId,
          Title: editTitle.trim(),
          Description: editDescription ?? "",
          sourceType: editSourceType?.value,
          sourceRef: editSourceRef.trim(),
          risk: editRisk?.value,
          investigationSummary: (investigationDetails || "").trim(),
          assignedInvestigatorId: assignedInvestigator?.value || "",
          assignedInvestigatorName: assignedInvestigator?.label || "",
          ownerStaffId: assignedInvestigator?.value || detail?.OwnerStaffId || "",
          ownerName: assignedInvestigator?.label || detail?.OwnerName || "",
          supportingEvidence: "",
          supportingEvidenceFiles: JSON.stringify(nextEvidence),
          rootCauseSummary: (rcaDetails || "").trim(),
          rcaMethod: rcaMethod?.value || "",
          rcaEvidenceFiles: JSON.stringify(rcaEvidenceFiles),
        }),
        userData,
      );
      await reload();
      await loadAuditLog();
    } catch (e) {
      setErr(e?.message || "Save failed");
      await reload();
      await loadAuditLog();
    } finally {
      setBusy(false);
    }
  }, [
    canEditInvestigation,
    pendingEvidenceFiles,
    pendingEvidenceNotes,
    evidenceFiles,
    capaRefId,
    userData,
    editTitle,
    editDescription,
    editSourceType,
    editSourceRef,
    editRisk,
    investigationDetails,
    assignedInvestigator,
    detail?.OwnerStaffId,
    detail?.OwnerName,
    reload,
    loadAuditLog,
  ]);

  const closeEvidenceModal = useCallback(() => {
    setEvidenceModalMode(null);
    setPendingEvidenceNotes("");
    setPendingEvidenceFiles([]);
    setEvidenceUploadKey((k) => k + 1);
    setErr(null);
  }, []);

  const handleRcaEvidenceFileUploadSelect = useCallback(
    (data) => {
      if (!canEditRca) return;
      const batch = Array.isArray(data) ? data : data ? [data] : [];
      if (!batch.length) return;
      setErr(null);
      setPendingRcaEvidenceFiles(batch);
    },
    [canEditRca],
  );

  const handleRcaEvidenceUploadError = useCallback((error) => {
    const msg =
      error?.message ||
      (typeof error === "string" ? error : "File upload error");
    setErr(msg);
  }, []);

  const doRemoveRcaEvidenceFile = useCallback(
    async (fileId) => {
      if (!canEditRca) return;
      const next = rcaEvidenceFiles.filter((f) => f.id !== fileId);
      setErr(null);
      setRcaEvidenceFiles(next);
      setBusy(true);
      try {
        await saveCapa(
          omitInvestigationAndClosureFromCapaPayload({
            CapaId: capaRefId,
            Title: editTitle.trim(),
            Description: editDescription ?? "",
            sourceType: editSourceType?.value,
            sourceRef: editSourceRef.trim(),
            risk: editRisk?.value,
            investigationSummary: (investigationDetails || "").trim(),
            assignedInvestigatorId: assignedInvestigator?.value || "",
            assignedInvestigatorName: assignedInvestigator?.label || "",
            ownerStaffId: assignedInvestigator?.value || detail?.OwnerStaffId || "",
            ownerName: assignedInvestigator?.label || detail?.OwnerName || "",
            supportingEvidence: "",
            supportingEvidenceFiles: JSON.stringify(evidenceFiles),
            rootCauseSummary: (rcaDetails || "").trim(),
            rcaMethod: rcaMethod?.value || "",
            rcaEvidenceFiles: JSON.stringify(next),
          }),
          userData,
        );
        await reload();
      } catch (e) {
        setErr(e?.message || "Save failed");
        await reload();
      } finally {
        setBusy(false);
      }
    },
    [
      canEditRca,
      rcaEvidenceFiles,
      evidenceFiles,
      capaRefId,
      userData,
      editTitle,
      editDescription,
      editSourceType,
      editSourceRef,
      editRisk,
      assignedInvestigator,
      detail?.OwnerStaffId,
      detail?.OwnerName,
      rcaDetails,
      rcaMethod,
      reload,
    ],
  );

  const confirmRemoveRcaEvidenceFile = useCallback(
    (fileId) => {
      showConfirm({
        title: "Remove evidence",
        message: "Are you sure you want to delete this row?",
        confirmText: "Yes, delete",
        cancelText: "Cancel",
        confirmButtonClass: "btn-danger",
        icon: "fas fa-exclamation-circle text-warning",
        onConfirm: () => {
          void doRemoveRcaEvidenceFile(fileId);
        },
      });
    },
    [showConfirm, doRemoveRcaEvidenceFile],
  );

  const evidenceTableActions = useMemo(
    () => [
      {
        label: "View file",
        className: "btn-outline-primary",
        visible: (row) => Boolean(row.dataUrl),
        onClick: (row) => {
          window.open(row.dataUrl, "_blank", "noopener,noreferrer");
        },
      },
      ...(canEditInvestigation
        ? [
            {
              label: "Remove",
              className: "btn-outline-danger",
              onClick: (row) => {
                confirmRemoveEvidenceFile(row.id);
              },
            },
          ]
        : []),
    ],
    [canEditInvestigation, confirmRemoveEvidenceFile],
  );

  const renderInvestigationEvidenceList = useCallback(
    (rows) => (
      <div className="capa-investigation-evidence-list">
        {rows.length === 0 ? (
          <div className="text-muted small">No evidence added yet.</div>
        ) : (
          rows.map((row) => (
            <div key={row.id} className="capa-investigation-evidence-list__item">
              <div className="capa-investigation-evidence-list__meta">
                <div className="capa-investigation-evidence-list__line">
                  <span className="capa-investigation-evidence-list__label">FILE</span>
                  <HoverPreview
                    content={row.fileName || "—"}
                    plainTextForPreview={row.fileName || "—"}
                    isHtml={false}
                    previewLength={26}
                    maxWidth={260}
                    maxHeight={140}
                    appendEllipsis
                    className="capa-investigation-evidence-list__name"
                  />
                </div>
                <div className="capa-investigation-evidence-list__line">
                  <span className="capa-investigation-evidence-list__label">NOTES</span>
                  <HoverPreview
                    content={row.notes || "—"}
                    plainTextForPreview={row.notes || "—"}
                    isHtml={false}
                    previewLength={26}
                    maxWidth={260}
                    maxHeight={140}
                    appendEllipsis
                    className="capa-investigation-evidence-list__notes"
                  />
                </div>
              </div>
              <div className="capa-investigation-evidence-list__footer">
                <span className="capa-investigation-evidence-list__datetime">
                  {row.addedAtLabel || "—"}
                </span>
                <div className="capa-investigation-evidence-list__actions">
                  {row.dataUrl ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-link p-0 capa-investigation-evidence-list__icon-btn"
                      title="View"
                      aria-label="View"
                      onClick={() =>
                        window.open(row.dataUrl, "_blank", "noopener,noreferrer")
                      }
                    >
                      <i className="fas fa-eye" aria-hidden="true" />
                    </button>
                  ) : null}
                  {canEditInvestigation ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-danger p-0 capa-investigation-evidence-list__icon-btn"
                      title="Remove"
                      aria-label="Remove"
                      onClick={() => confirmRemoveEvidenceFile(row.id)}
                    >
                      <i className="fas fa-trash-alt" aria-hidden="true" />
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    ),
    [canEditInvestigation, confirmRemoveEvidenceFile],
  );

  const renderRcaEvidenceList = useCallback(
    (rows) => (
      <div className="capa-investigation-evidence-list">
        {rows.length === 0 ? (
          <div className="text-muted small">No evidence added yet.</div>
        ) : (
          rows.map((row) => (
            <div key={row.id} className="capa-investigation-evidence-list__item">
              <div className="capa-investigation-evidence-list__meta">
                <div className="capa-investigation-evidence-list__line">
                  <span className="capa-investigation-evidence-list__label">FILE</span>
                  <HoverPreview
                    content={row.fileName || "—"}
                    plainTextForPreview={row.fileName || "—"}
                    isHtml={false}
                    previewLength={26}
                    maxWidth={260}
                    maxHeight={140}
                    appendEllipsis
                    className="capa-investigation-evidence-list__name"
                  />
                </div>
                <div className="capa-investigation-evidence-list__line">
                  <span className="capa-investigation-evidence-list__label">NOTES</span>
                  <HoverPreview
                    content={row.notes || "—"}
                    plainTextForPreview={row.notes || "—"}
                    isHtml={false}
                    previewLength={26}
                    maxWidth={260}
                    maxHeight={140}
                    appendEllipsis
                    className="capa-investigation-evidence-list__notes"
                  />
                </div>
              </div>
              <div className="capa-investigation-evidence-list__footer">
                <span className="capa-investigation-evidence-list__datetime">
                  {row.addedAtLabel || "—"}
                </span>
                <div className="capa-investigation-evidence-list__actions">
                  {row.dataUrl ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-link p-0 capa-investigation-evidence-list__icon-btn"
                      title="View"
                      aria-label="View"
                      onClick={() =>
                        window.open(row.dataUrl, "_blank", "noopener,noreferrer")
                      }
                    >
                      <i className="fas fa-eye" aria-hidden="true" />
                    </button>
                  ) : null}
                  {canEditRca ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-danger p-0 capa-investigation-evidence-list__icon-btn"
                      title="Remove"
                      aria-label="Remove"
                      onClick={() => confirmRemoveRcaEvidenceFile(row.id)}
                    >
                      <i className="fas fa-trash-alt" aria-hidden="true" />
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    ),
    [canEditRca, confirmRemoveRcaEvidenceFile],
  );

  const rcaEvidenceTableActions = useMemo(
    () => [
      {
        label: "View file",
        className: "btn-outline-primary",
        visible: (row) => Boolean(row.dataUrl),
        onClick: (row) => {
          window.open(row.dataUrl, "_blank", "noopener,noreferrer");
        },
      },
      ...(canEditRca
        ? [
            {
              label: "Remove",
              className: "btn-outline-danger",
              onClick: (row) => {
                confirmRemoveRcaEvidenceFile(row.id);
              },
            },
          ]
        : []),
    ],
    [canEditRca, confirmRemoveRcaEvidenceFile],
  );

  const doRemoveClosureEvidenceFile = useCallback(
    async (fileId) => {
      if (!canEditClosure) return;
      const next = closureEvidenceFiles.filter((f) => f.id !== fileId);
      setErr(null);
      setClosureEvidenceFiles(next);
      setBusy(true);
      try {
        await saveCapa(
          omitRcaAndInvestigationFromCapaPayload({
            CapaId: capaRefId,
            Title: editTitle.trim(),
            Description: editDescription ?? "",
            sourceType: editSourceType?.value,
            sourceRef: editSourceRef.trim(),
            risk: editRisk?.value,
            investigationSummary: (investigationDetails || "").trim(),
            assignedInvestigatorId: assignedInvestigator?.value || "",
            assignedInvestigatorName: assignedInvestigator?.label || "",
            ownerStaffId: assignedInvestigator?.value || detail?.OwnerStaffId || "",
            ownerName: assignedInvestigator?.label || detail?.OwnerName || "",
            supportingEvidence: "",
            supportingEvidenceFiles: JSON.stringify(evidenceFiles),
            rootCauseSummary: (rcaDetails || "").trim(),
            rcaMethod: rcaMethod?.value || "",
            rcaEvidenceFiles: JSON.stringify(rcaEvidenceFiles),
            ClosureRemarks: closureRemarks,
            ClosureEvidenceFiles: JSON.stringify(next),
          }),
          userData,
        );
        await reload();
      } catch (e) {
        setErr(e?.message || "Save failed");
        await reload();
      } finally {
        setBusy(false);
      }
    },
    [
      canEditClosure,
      closureEvidenceFiles,
      closureRemarks,
      capaRefId,
      userData,
      editTitle,
      editDescription,
      editSourceType,
      editSourceRef,
      editRisk,
      assignedInvestigator,
      detail?.OwnerStaffId,
      detail?.OwnerName,
      reload,
    ],
  );

  const confirmRemoveClosureEvidenceFile = useCallback(
    (fileId) => {
      showConfirm({
        title: "Remove attachment",
        message: "Remove this file from closure attachments?",
        confirmText: "Yes, remove",
        cancelText: "Cancel",
        confirmButtonClass: "btn-danger",
        icon: "fas fa-exclamation-circle text-warning",
        onConfirm: () => {
          void doRemoveClosureEvidenceFile(fileId);
        },
      });
    },
    [showConfirm, doRemoveClosureEvidenceFile],
  );

  const handleAddRcaEvidenceRow = useCallback(async () => {
    if (!canEditRca) {
      setErr(
        "RCA evidence cannot be saved in the current CAPA status. Open the RCA step while the CAPA is in an editable RCA stage.",
      );
      return;
    }
    if (!String(capaRefId || "").trim()) {
      setErr("Cannot save evidence: CAPA id is missing. Refresh the page and try again.");
      return;
    }
    const note = String(pendingRcaEvidenceNotes || "").trim();
    const hasNote = Boolean(note);
    const hasFile = pendingRcaEvidenceFiles.length > 0;
    if (!hasNote && !hasFile) {
      setErr("Add a file or notes before clicking Add.");
      return;
    }
    const remaining = MAX_EVIDENCE_FILES - rcaEvidenceFiles.length;
    if (remaining <= 0) {
      setErr(`Maximum ${MAX_EVIDENCE_FILES} evidence rows`);
      return;
    }
    let additions = [];
    if (hasFile) {
      additions = pendingRcaEvidenceFiles.map((f) => ({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
        createdAt: new Date().toISOString(),
        name: f.name,
        size: f.size,
        type: f.type || "",
        dataUrl: `data:${f.type || "application/octet-stream"};base64,${f.data}`,
        notes: note,
      }));
    } else {
      additions = [
        {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
          createdAt: new Date().toISOString(),
          name: "",
          size: null,
          type: "",
          dataUrl: "",
          notes: note,
        },
      ];
    }
    if (additions.length > remaining) {
      setErr(
        `Too many files for the remaining slots (max ${MAX_EVIDENCE_FILES} rows, ${remaining} left). Remove a row or add fewer files.`,
      );
      return;
    }
    const slice = additions;
    const nextRcaEvidence = [...rcaEvidenceFiles, ...slice];
    setErr(null);
    setRcaEvidenceFiles(nextRcaEvidence);
    setPendingRcaEvidenceFiles([]);
    setPendingRcaEvidenceNotes("");
    setRcaEvidenceUploadKey((k) => k + 1);
    setRcaEvidenceModalMode(null);
    setBusy(true);
    try {
      await saveCapa(
        omitInvestigationAndClosureFromCapaPayload({
          CapaId: capaRefId,
          Title: editTitle.trim(),
          Description: editDescription ?? "",
          sourceType: editSourceType?.value,
          sourceRef: editSourceRef.trim(),
          risk: editRisk?.value,
          investigationSummary: (investigationDetails || "").trim(),
          assignedInvestigatorId: assignedInvestigator?.value || "",
          assignedInvestigatorName: assignedInvestigator?.label || "",
          ownerStaffId: assignedInvestigator?.value || detail?.OwnerStaffId || "",
          ownerName: assignedInvestigator?.label || detail?.OwnerName || "",
          supportingEvidence: "",
          supportingEvidenceFiles: JSON.stringify(evidenceFiles),
          rootCauseSummary: (rcaDetails || "").trim(),
          rcaMethod: rcaMethod?.value || "",
          rcaEvidenceFiles: JSON.stringify(nextRcaEvidence),
        }),
        userData,
      );
      await reload();
    } catch (e) {
      setErr(e?.message || "Save failed");
      await reload();
    } finally {
      setBusy(false);
    }
  }, [
    canEditRca,
    pendingRcaEvidenceFiles,
    pendingRcaEvidenceNotes,
    rcaEvidenceFiles,
    capaRefId,
    userData,
    editTitle,
    editDescription,
    editSourceType,
    editSourceRef,
    editRisk,
    assignedInvestigator,
    detail?.OwnerStaffId,
    detail?.OwnerName,
    rcaDetails,
    rcaMethod,
    reload,
    evidenceFiles,
  ]);

  const closeRcaEvidenceModal = useCallback(() => {
    setRcaEvidenceModalMode(null);
    setPendingRcaEvidenceNotes("");
    setPendingRcaEvidenceFiles([]);
    setRcaEvidenceUploadKey((k) => k + 1);
    setErr(null);
  }, []);

  const handleSubmitActionPlan = useCallback(() => {
    if (!rcaComplete) {
      setErr("Please select an RCA method and enter RCA details.");
      return;
    }
    showConfirm({
      title: "Submit for Action Plan",
      message:
        "Submit and move CAPA to the Action Planning stage?",
      confirmText: "Yes, submit",
      cancelText: "Cancel",
      confirmButtonClass: "btn-primary",
      icon: "fas fa-tasks text-warning",
      onConfirm: async () => {
        const authRes = await openAuthModal({
          showDescription: true,
          requireDescription: true,
        });
        if (authRes?.Success !== 1) {
          if (!authRes?.cancelled) {
            setErr("Two-factor authentication failed or cancelled.");
          }
          return;
        }
        const reason = String(authRes?.Description || "").trim();
        if (!reason) {
          setErr("Reason is required in the auth modal.");
          return;
        }
        await runTransition(WORKFLOW_ACTIONS.SUBMIT_ACTION_PLAN, reason);
      },
    });
  }, [rcaComplete, showConfirm]);

  const handleUpdateDetail = useCallback(async () => {
    setErr(null);
    setBusy(true);
    try {
      await saveDetailFieldsOnly();
      await reload();
    } catch (e) {
      setErr(e?.message || "Save failed");
    } finally {
      setBusy(false);
    }
  }, [saveDetailFieldsOnly, reload]);

  const runTransition = async (action, reason) => {
    setErr(null);
    setSuccessMsg("");
    setBusy(true);
    try {
      if ((canManageCapaStage || canEditThisCapa) && anyCapaDirty) {
        await saveDetailFieldsOnly();
      }
      const res = await executeCapaTransition(
        capaRefId,
        action,
        userData,
        reason,
      );
      if (!res?.ok) {
        setErr(res?.message || "Transition failed");
        await loadAuditLog();
        return;
      }
      const d = await reload();
      if (d) {
        /* Refetch stage lists (e.g. approvers) after status change — cache would skip load otherwise. */
        loadedStageDataRef.current = {
          actions: false,
          approvers: false,
          implementation: false,
          verification: false,
        };
        setSelectedStepIndex(getStageIndex(d.Status));
      }
      await loadAuditLog();
    } finally {
      setBusy(false);
    }
  };

  const handleSubmitForInvestigationTransition = useCallback(() => {
    if (!capaDetailsAreComplete(detail)) {
      setErr("Add title and description first.");
      return;
    }
    showConfirm({
      title: "Submit for Investigation",
      message: "Submit and move CAPA to the Investigation stage?",
      confirmText: "Yes, submit",
      cancelText: "Cancel",
      confirmButtonClass: "btn-primary",
      icon: "fas fa-search text-warning",
      onConfirm: async () => {
        const authRes = await openAuthModal({
          showDescription: true,
          requireDescription: true,
        });
        if (authRes?.Success !== 1) {
          if (!authRes?.cancelled) {
            setErr("Two-factor authentication failed or cancelled.");
          }
          return;
        }
        const reason = String(authRes?.Description || "").trim();
        if (!reason) {
          setErr("Reason is required in the auth modal.");
          return;
        }
        await runTransition(WORKFLOW_ACTIONS.SUBMIT_INVESTIGATION, reason);
      },
    });
  }, [detail, showConfirm, runTransition]);

  const handleSendForApproval = useCallback(() => {
    if (!allTasksReviewed) {
      showTaskAlert({
        title: "Action planning",
        message:
          "All assigned staff must accept or reject their tasks before sending for approval.",
        type: "warning",
      });
      return;
    }
    showConfirm({
      title: "Send for Approval",
      message:
        "All task decisions are complete. Send this CAPA to the Approval stage?",
      confirmText: "Yes, send",
      cancelText: "Cancel",
      confirmButtonClass: "btn-primary",
      icon: "fas fa-check-circle text-warning",
      onConfirm: async () => {
        const authRes = await openAuthModal({
          showDescription: true,
          requireDescription: true,
        });
        if (authRes?.Success !== 1) {
          if (!authRes?.cancelled) {
            showTaskAlert({
              title: "Action planning",
              message: "Two-factor authentication failed or cancelled.",
              type: "warning",
            });
          }
          return;
        }
        const reason = String(authRes?.Description || "").trim();
        if (!reason) {
          showTaskAlert({
            title: "Action planning",
            message: "Reason is required in the auth modal.",
            type: "warning",
          });
          return;
        }
        await runTransition(WORKFLOW_ACTIONS.APPROVE, reason);
      },
    });
  }, [allTasksReviewed, showTaskAlert, showConfirm, runTransition]);

  const handleSendForImplementation = useCallback(() => {
    if (!allApproverRecordsReviewed) {
      showApproverRecordAlert({
        title: "Approval",
        message:
          "All approvers must accept or reject before sending for implementation.",
        type: "warning",
      });
      return;
    }
    showConfirm({
      title: "Send for Implementation",
      message:
        "All approver decisions are complete. Move this CAPA to the Implementation stage?",
      confirmText: "Yes, send",
      cancelText: "Cancel",
      confirmButtonClass: "btn-primary",
      icon: "fas fa-check-circle text-warning",
      onConfirm: async () => {
        const authRes = await openAuthModal({
          showDescription: true,
          requireDescription: true,
        });
        if (authRes?.Success !== 1) {
          if (!authRes?.cancelled) {
            showApproverRecordAlert({
              title: "Approval",
              message: "Two-factor authentication failed or cancelled.",
              type: "warning",
            });
          }
          return;
        }
        const reason = String(authRes?.Description || "").trim();
        if (!reason) {
          showApproverRecordAlert({
            title: "Approval",
            message: "Reason is required in the auth modal.",
            type: "warning",
          });
          return;
        }
        await runTransition(WORKFLOW_ACTIONS.MARK_COMPLETED, reason);
      },
    });
  }, [allApproverRecordsReviewed, showApproverRecordAlert, showConfirm, runTransition]);

  const handleSendToVerification = useCallback(() => {
    if (!allImplementationTasksReadyForVerification) {
      showImplTaskAlert({
        title: "Implementation",
        message:
          "Every implementation task must be Completed, Partially Completed, or Deferred before sending to Verification. Not Started and In Progress are not allowed.",
        type: "warning",
      });
      return;
    }
    showConfirm({
      title: "Send to Verification",
      message:
        "All implementation tasks have been updated. Move this CAPA to the Verification stage?",
      confirmText: "Yes, send",
      cancelText: "Cancel",
      confirmButtonClass: "btn-primary",
      icon: "fas fa-clipboard-check text-warning",
      onConfirm: async () => {
        const authRes = await openAuthModal({
          showDescription: true,
          requireDescription: true,
        });
        if (authRes?.Success !== 1) {
          if (!authRes?.cancelled) {
            showImplTaskAlert({
              title: "Implementation",
              message: "Two-factor authentication failed or cancelled.",
              type: "warning",
            });
          }
          return;
        }
        const reason = String(authRes?.Description || "").trim();
        if (!reason) {
          showImplTaskAlert({
            title: "Implementation",
            message: "Reason is required in the auth modal.",
            type: "warning",
          });
          return;
        }
        await runTransition(WORKFLOW_ACTIONS.SEND_TO_VERIFICATION, reason);
      },
    });
  }, [
    allImplementationTasksReadyForVerification,
    showImplTaskAlert,
    showConfirm,
    runTransition,
  ]);

  const handleDeleteTask = useCallback(
    (actionItemId) => {
      if (!detail?.CapaId || !actionItemId) return;
      showConfirm({
        title: "Remove task",
        message: "Are you sure you want to delete this action-planning task?",
        confirmText: "Yes, delete",
        cancelText: "Cancel",
        confirmButtonClass: "btn-danger",
        icon: "fas fa-exclamation-circle text-warning",
        onConfirm: async () => {
          try {
            const updated = await deleteCapaActionItem(detail.CapaId, actionItemId, userData);
            setActions(updated);
          } catch (e) {
            showTaskAlert({
              title: "Action planning",
              message: e?.message || "Unable to delete task.",
              type: "warning",
            });
          }
        },
      });
    },
    [detail?.CapaId, showConfirm, showTaskAlert, userData],
  );

  const resetNewApproverForm = () => {
    setNewApproverDescription("");
    setNewApproverDueDate("");
    setNewApproverStaff([]);
  };

  const handleAddApproverRecord = useCallback(async () => {
    if (!canEditApprovalPlan) return;
    hideApproverRecordAlert();
    const descHtml = String(newApproverDescription || "").trim();
    const descPlain = stripHtml(descHtml).trim();
    if (!descPlain) {
      showApproverRecordAlert({
        title: "Approval",
        message: "Enter a description before adding an approver record.",
        type: "warning",
      });
      return;
    }
    if (!newApproverDueDate) {
      showApproverRecordAlert({
        title: "Approval",
        message: "Select a due date.",
        type: "warning",
      });
      return;
    }
    const selectedApprovers = Array.isArray(newApproverStaff)
      ? newApproverStaff.filter((s) => s?.value)
      : newApproverStaff?.value
        ? [newApproverStaff]
        : [];
    if (!selectedApprovers.length) {
      showApproverRecordAlert({
        title: "Approval",
        message: "Select at least one approver (staff).",
        type: "warning",
      });
      return;
    }
    setAddingApproverRecord(true);
    try {
      let updatedList = approverRecords;
      for (const approver of selectedApprovers) {
        const payload = {
          Description: descHtml,
          DueDate: newApproverDueDate,
          ApproverStaffId: approver.value,
          ApproverName: approver.label,
          ApproverEmailId: approver.emailId || "",
          Status: "N/A",
        };
        const capaKey = detail?.CapaId || id;
        // Create one row per selected approver so table shows separate entries.
        updatedList = await createCapaApproverRecord(
          capaKey,
          payload,
          userData,
          capaModuleApiOpts,
        );
      }
      setApproverRecords(updatedList);
      resetNewApproverForm();
      setApprovalRecordModalOpen(false);
    } catch (e) {
      setErr(e?.message || "Failed to add approver record.");
    } finally {
      setAddingApproverRecord(false);
    }
  }, [
    canEditApprovalPlan,
    newApproverDescription,
    newApproverDueDate,
    newApproverStaff,
    approverRecords,
    detail?.CapaId,
    id,
    userData,
    capaModuleApiOpts,
    hideApproverRecordAlert,
    showApproverRecordAlert,
  ]);

  const handleApproverRecordDecision = useCallback(
    async (recordId, nextStatus) => {
      if (!detail?.CapaId || !recordId) return;
      const authRes = await openAuthModal({
        showDescription: true,
        requireDescription: true,
      });
      if (authRes?.Success !== 1) {
        if (!authRes?.cancelled) {
          showApproverRecordAlert({
            title: "Approval",
            message: "Two-factor authentication failed or cancelled.",
            type: "warning",
          });
        }
        return;
      }
      const reason = String(authRes?.Description || "").trim();
      if (!reason) {
        showApproverRecordAlert({
          title: "Approval",
          message: "Reason is required in auth modal.",
          type: "warning",
        });
        return;
      }
      setUpdatingApproverRecordId(recordId);
      try {
        const updated = await updateCapaApproverRecordStatus(
          detail?.CapaId || id,
          recordId,
          nextStatus,
          reason,
          userData,
          capaModuleApiOpts,
        );
        setApproverRecords(updated);
        const auditRows = await fetchCapaAuditLogForCapa(
          detail?.CapaId || id,
          userData,
          capaModuleApiOpts,
        );
        setAuditLog(auditRows);
      } catch (e) {
        showApproverRecordAlert({
          title: "Approval",
          message: e?.message || "Unable to update approval record.",
          type: "warning",
        });
      } finally {
        setUpdatingApproverRecordId("");
      }
    },
    [detail?.CapaId, id, showApproverRecordAlert, userData, capaModuleApiOpts],
  );

  const handleDeleteApproverRecord = useCallback(
    (recordId) => {
      if (!detail?.CapaId || !recordId) return;
      showConfirm({
        title: "Remove approval record",
        message: "Are you sure you want to delete this approver assignment?",
        confirmText: "Yes, delete",
        cancelText: "Cancel",
        confirmButtonClass: "btn-danger",
        icon: "fas fa-exclamation-circle text-warning",
        onConfirm: async () => {
          try {
            const updated = await deleteCapaApproverRecord(
              detail.CapaId,
              recordId,
              userData,
              capaModuleApiOpts,
            );
            setApproverRecords(updated);
          } catch (e) {
            showApproverRecordAlert({
              title: "Approval",
              message: e?.message || "Unable to delete approval record.",
              type: "warning",
            });
          }
        },
      });
    },
    [detail?.CapaId, showConfirm, showApproverRecordAlert, capaModuleApiOpts],
  );

  const handleAddImplementationTask = useCallback(async () => {
    if (!canEditImplementation) return;
    hideImplTaskAlert();
    const descHtml = String(newImplDescription || "").trim();
    const descPlain = stripHtml(descHtml).trim();
    if (!descPlain) {
      showImplTaskAlert({
        title: "Implementation",
        message: "Enter task details before adding.",
        type: "warning",
      });
      return;
    }
    if (!newImplDueDate) {
      showImplTaskAlert({
        title: "Implementation",
        message: "Select a due date.",
        type: "warning",
      });
      return;
    }
    if (!newImplStaff?.value) {
      showImplTaskAlert({
        title: "Implementation",
        message: "Select staff to assign the task.",
        type: "warning",
      });
      return;
    }
    setAddingImplTask(true);
    try {
      const updated = await createCapaImplementationTask(
        detail.CapaId,
        {
          Description: descHtml,
          DueDate: newImplDueDate,
          AssignedStaffId: newImplStaff.value,
          AssignedName: newImplStaff.label,
          ImplementationStatus: "Not Started",
        },
        userData,
        capaModuleApiOpts,
      );
      setImplementationTasks(updated);
      const m = {};
      updated.forEach((t) => {
        m[t.ImplementationTaskId] = t.ImplementationStatus;
      });
      setImplStatusDraft(m);
      setNewImplDescription("");
      setNewImplDueDate("");
      setNewImplStaff(null);
      setImplTaskModalOpen(false);
    } catch (e) {
      setErr(e?.message || "Failed to add implementation task.");
    } finally {
      setAddingImplTask(false);
    }
  }, [
    canEditImplementation,
    newImplDescription,
    newImplDueDate,
    newImplStaff,
    detail?.CapaId,
    userData,
    capaModuleApiOpts,
    hideImplTaskAlert,
    showImplTaskAlert,
  ]);

  const handleSaveImplementationStatus = useCallback(
    async (taskId) => {
      if (!detail?.CapaId || !taskId) return;
      const task = implementationTasks.find(
        (t) => t.ImplementationTaskId === taskId,
      );
      if (!task) return;
      if (String(task.ImplementationStatus || "").trim() === "Completed") {
        showImplTaskAlert({
          title: "Implementation",
          message: "This task is already Completed and cannot be changed.",
          type: "warning",
        });
        return;
      }
      const next =
        implStatusDraft[taskId] ?? task.ImplementationStatus;
      if (next === task.ImplementationStatus) {
        showImplTaskAlert({
          title: "Implementation",
          message:
            "Choose a different implementation status, then save (description is required in the auth modal).",
          type: "warning",
        });
        return;
      }
      hideImplTaskAlert();
      const authRes = await openAuthModal({
        showDescription: true,
        requireDescription: true,
      });
      if (authRes?.Success !== 1) {
        if (!authRes?.cancelled) {
          showImplTaskAlert({
            title: "Implementation",
            message: "Authentication failed or cancelled.",
            type: "warning",
          });
        }
        return;
      }
      const reason = String(authRes?.Description || "").trim();
      if (!reason) {
        showImplTaskAlert({
          title: "Implementation",
          message: "Description is required in the auth modal.",
          type: "warning",
        });
        return;
      }
      setUpdatingImplTaskId(taskId);
      try {
        const updated = await updateCapaImplementationTaskStatus(
          detail.CapaId,
          taskId,
          next,
          reason,
          userData,
          capaModuleApiOpts,
        );
        setImplementationTasks(updated);
        setImplStatusDraft((d) => ({ ...d, [taskId]: next }));
      } catch (e) {
        showImplTaskAlert({
          title: "Implementation",
          message: e?.message || "Unable to update implementation status.",
          type: "warning",
        });
      } finally {
        setUpdatingImplTaskId("");
      }
    },
    [
      detail?.CapaId,
      implementationTasks,
      implStatusDraft,
      hideImplTaskAlert,
      showImplTaskAlert,
      userData,
      capaModuleApiOpts,
    ],
  );

  const handleDeleteImplementationTask = useCallback(
    (taskId) => {
      if (!detail?.CapaId || !taskId) return;
      const task = implementationTasks.find(
        (t) => t.ImplementationTaskId === taskId,
      );
      if (
        task?.StatusUpdatedAt != null &&
        String(task.StatusUpdatedAt).trim() !== ""
      ) {
        showImplTaskAlert({
          title: "Implementation",
          message:
            "This task cannot be removed after an implementation status has been saved.",
          type: "warning",
        });
        return;
      }
      showConfirm({
        title: "Remove implementation task",
        message: "Delete this implementation task?",
        confirmText: "Yes, delete",
        cancelText: "Cancel",
        confirmButtonClass: "btn-danger",
        icon: "fas fa-exclamation-circle text-warning",
        onConfirm: async () => {
          try {
            const updated = await deleteCapaImplementationTask(
              detail.CapaId,
              taskId,
              userData,
              capaModuleApiOpts,
            );
            setImplementationTasks(updated);
            const m = {};
            updated.forEach((t) => {
              m[t.ImplementationTaskId] = t.ImplementationStatus;
            });
            setImplStatusDraft(m);
          } catch (e) {
            showImplTaskAlert({
              title: "Implementation",
              message: e?.message || "Unable to delete task.",
              type: "warning",
            });
          }
        },
      });
    },
    [detail?.CapaId, implementationTasks, showConfirm, showImplTaskAlert, userData, capaModuleApiOpts],
  );

  const handleVerifierFileSelect = useCallback(
    (requestId) => (data) => {
      if (!detail?.CapaId || !requestId) return;
      const batch = Array.isArray(data) ? data : data ? [data] : [];
      if (!batch.length) return;
      setVerifierDraft((prev) => {
        const cur = prev[requestId] || {
          outcome: "",
          comments: "",
          files: [],
        };
        const remaining = MAX_EVIDENCE_FILES - cur.files.length;
        if (remaining <= 0) return prev;
        const additions = batch.slice(0, remaining).map((f) => ({
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
          name: f.name,
          size: f.size,
          type: f.type || "",
          dataUrl: `data:${f.type || "application/octet-stream"};base64,${f.data}`,
          notes: "",
        }));
        return {
          ...prev,
          [requestId]: {
            ...cur,
            files: [...cur.files, ...additions],
          },
        };
      });
      setVerifierUploadKey((k) => ({
        ...k,
        [requestId]: (k[requestId] || 0) + 1,
      }));
    },
    [detail?.CapaId],
  );

  const handleRemoveVerifierFile = useCallback((requestId, fileId) => {
    setVerifierDraft((prev) => {
      const cur = prev[requestId];
      if (!cur) return prev;
      return {
        ...prev,
        [requestId]: {
          ...cur,
          files: cur.files.filter((f) => f.id !== fileId),
        },
      };
    });
  }, []);

  const handleVerifierUploadError = useCallback(
    (error) => {
      const msg =
        error?.message ||
        (typeof error === "string" ? error : "File upload error");
      showVerificationAlert({
        title: "Verification",
        message: msg,
        type: "warning",
      });
    },
    [showVerificationAlert],
  );

  const handleAddVerificationRequest = useCallback(async () => {
    if (!canEditVerification) return;
    hideVerificationAlert();
    const descHtml = String(newVerReqDescription || "").trim();
    const descPlain = stripHtml(descHtml).trim();
    if (!descPlain) {
      showVerificationAlert({
        title: "Verification",
        message: "Enter instructions for the verifier before adding.",
        type: "warning",
      });
      return;
    }
    if (!newVerReqStaff?.value) {
      showVerificationAlert({
        title: "Verification",
        message: "Select staff to assign the verification request.",
        type: "warning",
      });
      return;
    }
    const dueYmd = normalizeCapaApiDateYmd(newVerReqDueDate);
    if (!dueYmd) {
      showVerificationAlert({
        title: "Verification",
        message: "Select a due date for the verification request.",
        type: "warning",
      });
      return;
    }
    setAddingVerReq(true);
    try {
      const updated = await createCapaVerificationRequest(
        detail.CapaId,
        {
          Description: descHtml,
          DueDate: dueYmd,
          AssignedStaffId: newVerReqStaff.value,
          AssignedName: newVerReqStaff.label,
        },
        userData,
        capaModuleApiOpts,
      );
      setVerificationRequests(updated);
      setNewVerReqDescription("");
      setNewVerReqDueDate("");
      setNewVerReqStaff(null);
      setVerificationRequestModalOpen(false);
    } catch (e) {
      setErr(e?.message || "Failed to add verification request.");
    } finally {
      setAddingVerReq(false);
    }
  }, [
    canEditVerification,
    newVerReqDescription,
    newVerReqDueDate,
    newVerReqStaff,
    detail?.CapaId,
    userData,
    hideVerificationAlert,
    showVerificationAlert,
    capaModuleApiOpts,
  ]);

  const handleDeleteVerificationRequest = useCallback(
    (requestId) => {
      if (!detail?.CapaId || !requestId) return;
      showConfirm({
        title: "Remove verification request",
        message: "Delete this pending verification request?",
        confirmText: "Yes, delete",
        cancelText: "Cancel",
        confirmButtonClass: "btn-danger",
        icon: "fas fa-exclamation-circle text-warning",
        onConfirm: async () => {
          try {
            const updated = await deleteCapaVerificationRequest(
              detail.CapaId,
              requestId,
              userData,
              capaModuleApiOpts,
            );
            setVerificationRequests(updated);
            setVerificationVerifyOpenId((open) =>
              open === requestId ? null : open,
            );
            setVerifierDraft((prev) => {
              const next = { ...prev };
              delete next[requestId];
              return next;
            });
          } catch (e) {
            showVerificationAlert({
              title: "Verification",
              message: e?.message || "Unable to delete verification request.",
              type: "warning",
            });
          }
        },
      });
    },
    [detail?.CapaId, showConfirm, showVerificationAlert, userData, capaModuleApiOpts],
  );

  const handleCloseVerificationModal = useCallback(() => {
    if (submittingVerificationId) return;
    setVerificationVerifyOpenId(null);
  }, [submittingVerificationId]);

  const handleSubmitVerificationResponse = useCallback(
    async (requestId) => {
      if (!detail?.CapaId || !requestId) return;
      const draft = verifierDraft[requestId] || {
        outcome: "",
        comments: "",
        files: [],
      };
      const outcome = String(draft.outcome || "").trim();
      if (outcome !== "Effective" && outcome !== "Not Effective") {
        showVerificationAlert({
          title: "Verification",
          message: "Select Effective or Not Effective before submitting.",
          type: "warning",
        });
        return;
      }
      hideVerificationAlert();
      const authRes = await openAuthModal({ showDescription: false });
      if (authRes?.Success !== 1) {
        if (!authRes?.cancelled) {
          showVerificationAlert({
            title: "Verification",
            message: "Authentication failed or cancelled.",
            type: "warning",
          });
        }
        return;
      }
      setSubmittingVerificationId(requestId);
      try {
        const attachmentsJson = JSON.stringify(draft.files || []);
        const res = await submitCapaVerificationResponse(
          detail.CapaId,
          requestId,
          {
            Outcome: outcome,
            VerifierComments: String(draft.comments || "").trim(),
            VerificationAttachments: attachmentsJson,
            AuthAttestationNote: "",
          },
          userData,
          capaModuleApiOpts,
        );
        setVerificationRequests(res.requests);
        if (res.transition && !res.transition.ok) {
          showVerificationAlert({
            title: "Verification",
            message: res.transition.message || "Workflow transition failed.",
            type: "warning",
          });
        }
        const d = await reload();
        if (d) setSelectedStepIndex(getStageIndex(d.Status));
        await loadAuditLog();
        setVerificationVerifyOpenId(null);
        setVerifierDraft((prev) => {
          const next = { ...prev };
          delete next[requestId];
          return next;
        });
      } catch (e) {
        showVerificationAlert({
          title: "Verification",
          message: e?.message || "Unable to submit verification.",
          type: "warning",
        });
      } finally {
        setSubmittingVerificationId("");
      }
    },
    [
      detail?.CapaId,
      verifierDraft,
      hideVerificationAlert,
      showVerificationAlert,
      reload,
      userData,
      loadAuditLog,
      capaModuleApiOpts,
    ],
  );

  const handleClosureEvidenceFileSelect = useCallback(
    (data) => {
      if (!canEditClosure) return;
      const batch = Array.isArray(data) ? data : data ? [data] : [];
      if (!batch.length) return;
      setErr(null);
      setClosureEvidenceFiles((prev) => {
        const remaining = MAX_EVIDENCE_FILES - prev.length;
        if (remaining <= 0) return prev;
        const additions = batch.slice(0, remaining).map((f) => ({
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
          name: f.name,
          size: f.size,
          type: f.type || "",
          dataUrl: `data:${f.type || "application/octet-stream"};base64,${f.data}`,
          notes: "",
        }));
        return [...prev, ...additions];
      });
      setClosureEvidenceUploadKey((k) => k + 1);
    },
    [canEditClosure],
  );

  const handleClosureEvidenceUploadError = useCallback((error) => {
    const msg =
      error?.message ||
      (typeof error === "string" ? error : "File upload error");
    setErr(msg);
  }, []);

  const handleSendForClosure = useCallback(() => {
    if (!canSendForClosure) return;
    showConfirm({
      title: "Send for closure",
      message:
        "Move this CAPA to the Closure stage to enter final remarks and attachments?",
      confirmText: "Send for closure",
      cancelText: "Cancel",
      confirmButtonClass: "btn-primary",
      icon: "fas fa-flag-checkered text-primary",
      onConfirm: async () => {
        setErr(null);
        const authRes = await openAuthModal({
          showDescription: true,
          requireDescription: true,
        });
        if (authRes?.Success !== 1) {
          if (!authRes?.cancelled) {
            setErr("Two-factor authentication failed or cancelled.");
          }
          return;
        }
        const reason = String(authRes?.Description || "").trim();
        if (!reason) {
          setErr("Reason is required in the auth modal.");
          return;
        }
        await runTransition(WORKFLOW_ACTIONS.SEND_TO_CLOSURE, reason);
      },
    });
  }, [canSendForClosure, showConfirm, runTransition]);

  const handleCloseCapa = useCallback(async () => {
    const remarks = stripHtml(String(closureRemarks || "")).trim();
    if (!remarks) {
      setErr("Enter final remarks before closing the CAPA.");
      return;
    }
    setErr(null);
    setSuccessMsg("");
    const authRes = await openAuthModal({
      showDescription: true,
      requireDescription: true,
    });
    if (authRes?.Success !== 1) {
      if (!authRes?.cancelled) {
        setErr("Authentication failed or cancelled.");
      }
      return;
    }
    const reason = String(authRes?.Description || "").trim();
    if (!reason) {
      setErr("Reason is required in the auth modal.");
      return;
    }
    setBusy(true);
    try {
      await saveCapa(
        {
          CapaId: capaRefId,
          Title: editTitle.trim(),
          Description: editDescription ?? "",
          sourceType: editSourceType?.value,
          sourceRef: editSourceRef.trim(),
          risk: editRisk?.value,
          investigationSummary: (investigationDetails || "").trim(),
          assignedInvestigatorId: assignedInvestigator?.value || "",
          assignedInvestigatorName: assignedInvestigator?.label || "",
          ownerStaffId: assignedInvestigator?.value || detail?.OwnerStaffId || "",
          ownerName: assignedInvestigator?.label || detail?.OwnerName || "",
          supportingEvidence: "",
          supportingEvidenceFiles: JSON.stringify(evidenceFiles),
          rootCauseSummary: (rcaDetails || "").trim(),
          rcaMethod: rcaMethod?.value || "",
          rcaEvidenceFiles: JSON.stringify(rcaEvidenceFiles),
          ClosureRemarks: closureRemarks,
          ClosureEvidenceFiles: JSON.stringify(closureEvidenceFiles),
        },
        userData,
      );
      const res = await executeCapaTransition(
        capaRefId,
        WORKFLOW_ACTIONS.COMPLETE_CLOSURE,
        userData,
        reason,
      );
      if (!res?.ok) {
        setErr(res?.message || "Unable to close CAPA.");
        await loadAuditLog();
        return;
      }
      setSuccessMsg("CAPA closed.");
      const d = await reload();
      if (d) setSelectedStepIndex(getStageIndex(d.Status));
      await loadAuditLog();
    } catch (e) {
      setErr(e?.message || "Unable to close CAPA.");
    } finally {
      setBusy(false);
    }
  }, [
    closureRemarks,
    closureEvidenceFiles,
    capaRefId,
    userData,
    editTitle,
    editDescription,
    editSourceType,
    editSourceRef,
    editRisk,
    investigationDetails,
    assignedInvestigator,
    evidenceFiles,
    rcaDetails,
    rcaMethod,
    rcaEvidenceFiles,
    detail?.OwnerStaffId,
    detail?.OwnerName,
    reload,
    loadAuditLog,
  ]);

  const handleSubmitInvestigation = useCallback(() => {
    if (!investigationComplete) {
      setErr(
        "Please fill Investigation details and Investigator before submit.",
      );
      return;
    }
    showConfirm({
      title: "Submit Investigation",
      message:
        "Once submitted, investigation details become read-only and RCA stage will be enabled. Are you sure you want to submit?",
      confirmText: "Yes, Submit",
      cancelText: "No",
      confirmButtonClass: "btn-primary",
      icon: "fas fa-exclamation-circle text-warning",
      onConfirm: async () => {
        const authRes = await openAuthModal({
          showDescription: true,
          requireDescription: true,
        });
        // AuthModal passes API shape: Success (capital S), same as ISF flows.
        if (authRes?.Success !== 1) {
          if (!authRes?.cancelled) {
            setErr("Two-factor authentication failed or cancelled.");
          }
          return;
        }
        const reason = String(authRes?.Description || "").trim();
        if (!reason) {
          setErr("Reason is required in the auth modal.");
          return;
        }
        await runTransition(WORKFLOW_ACTIONS.SUBMIT_RCA, reason);
      },
    });
  }, [investigationComplete, showConfirm, runTransition]);

  if (!userData) {
    return (
      <div className="d-flex justify-content-center p-5">
        <div className="spinner-border" role="status" />
      </div>
    );
  }

  if (detailLoading && !detail) {
    return (
      <div className="capa-page">
        <PageBreadcrumb
          items={[
            { label: "Home", path: "/notification-center" },
            { label: "CAPA", path: "/capa" },
            { label: "Loading..." },
          ]}
        />
      </div>
    );
  }

  if (!detail && detailLoadAttempted) {
    return (
      <div className="capa-page">
        <PageBreadcrumb
          items={[
            { label: "Home", path: "/notification-center" },
            { label: "CAPA", path: "/capa" },
            { label: "Not found" },
          ]}
        />
        <div className="alert alert-warning mt-3">
          CAPA detail not found for this id/API response. Use{" "}
          <Link to="/capa">CAPA list</Link> and open a sample row.
        </div>
      </div>
    );
  }

  return (
    <div className="capa-page">
      <PageBreadcrumb
        items={[
          { label: "Home", path: "/notification-center" },
          { label: "CAPA", path: "/capa" },
          { label: detail.CapaNo },
        ]}
        toolbarExtras={
          <span
            className="badge rounded-pill"
            style={{
              ...getCapaStatusBadgeStyle(detail.Status),
              fontSize: "0.72rem",
              fontWeight: 700,
              lineHeight: 1.15,
              padding: "0.28em 0.7em",
            }}
          >
            {detail.Status || "—"}
          </span>
        }
      />

      {err && (
        <div className="alert alert-warning py-2" role="alert">
          {err}
        </div>
      )}
      {successMsg && (
        <div className="alert alert-success py-2" role="alert">
          {successMsg}
        </div>
      )}

      {/* Workflow Actions card (Approve/Reject etc.) intentionally hidden on detail view to avoid duplicate controls. */}

      {(() => {
        const cur = getStageIndex(detail.Status);
        const closureStageIdx = getStageIndex("Closure");
        /** Same step index as `Closure`; record status `Closed` maps here via getStageIndex. */
        const closedStageIdx = closureStageIdx;
        const phase =
          selectedStepIndex >= 0 && selectedStepIndex < CAPA_LIFECYCLE_PHASES.length
            ? CAPA_LIFECYCLE_PHASES[selectedStepIndex]
            : null;
        const pi = selectedStepIndex;
        const isCapaClosed = detail.Status === "Closed";
        /** Closed CAPA on last step: not “current” (no blue); neutral border. */
        const closedOnClosureStep = isCapaClosed && pi === closureStageIdx;
        const done = phase
          ? pi < cur || (isCapaClosed && pi === closureStageIdx)
          : false;
        const current = phase ? pi === cur && !isCapaClosed : false;
        const border = phase
          ? closedOnClosureStep
            ? "border-secondary"
            : done
              ? "border-success"
              : current
                ? "border-primary border-2"
                : "border-secondary"
          : "border-secondary";

        return (
          <div className="card mb-1" id="capa-detail-step-panel">
            <div className="card-header">

              <CapaStageStepper
                detail={detail}
                currentStatus={detail.Status}
                selectedStepIndex={selectedStepIndex}
                onStepClick={handleStepperStepClick}
              />
            </div>
            <div
              className={`card-body${
                selectedStepIndex === 1 || selectedStepIndex === 2
                  ? " capa-detail-step-body--investigation"
                  : ""
              }${
                selectedStepIndex >= 3
                  ? " capa-detail-step-body--workflow-split"
                  : ""
              }`}
            >
              {selectedStageDataLoading && (
                <div className="d-flex align-items-center text-muted small mb-3">
                  <div className="spinner-border spinner-border-sm me-2" role="status" />
                  Loading stage data...
                </div>
              )}
              {selectedStepIndex === 0 && detailFormOpts && (
                <div className="capa-detail-summary">
                  <div className="row g-3 capa-detail-summary__layout">
                    <div className="col-lg-9">
                      <div className="capa-detail-summary__shell" ref={detailSummaryShellRef}>

                    <div className="row g-3 capa-detail-summary__details-grid">
                      <div className="col-lg-7">
                        <div className="capa-detail-summary__left-column">
                          <div className="capa-detail-summary__title-row">
                            <h2 className="capa-detail-summary__title mb-0">
                              {!(detail.Title || "").trim() ? (
                                <span className="text-muted fw-normal">—</span>
                              ) : /<[^>]+>/.test(String(detail.Title)) ? (
                                <span
                                  dangerouslySetInnerHTML={{
                                    __html: String(detail.Title),
                                  }}
                                />
                              ) : (
                                stripHtml(String(detail.Title))
                              )}
                            </h2>
                            <span
                              className={`capa-detail-summary__risk-chip capa-detail-summary__risk-chip--${
                                String(detailFormOpts.riskLevel?.value || "low").toLowerCase()
                              }`}
                            >
                              <span className="capa-risk-pill__dot" aria-hidden />
                              {detailFormOpts.riskLevel?.label || "—"}
                            </span>
                          </div>
                          <div className="capa-detail-summary__description-label mt-3">
                            Description
                          </div>
                          {!stripHtml(String(detail.Description || "")).trim() ? (
                            <div className="capa-detail-summary__desc-html capa-detail-summary__desc-html--empty">
                              <span className="text-muted">—</span>
                            </div>
                          ) : (
                            <div
                              className="capa-detail-summary__desc-html"
                              dangerouslySetInnerHTML={{
                                __html: String(detail.Description || ""),
                              }}
                            />
                          )}
                        </div>
                      </div>
                      <div className="col-lg-5">
                        <div className="capa-detail-summary__side-stack">
                          <div className="capa-detail-summary__inline-block">
                            <div className="capa-detail-summary__card-title">
                              <i className="fas fa-tag me-2 text-secondary" aria-hidden />
                              Source
                            </div>
                            <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                              <span className="capa-detail-summary__chip">
                                {detailFormOpts.sourceType.label}
                              </span>
                              {String(detail.SourceRef || "").trim() ? (
                                <span className="capa-detail-summary__ref">{detail.SourceRef}</span>
                              ) : (
                                <span className="text-muted small">No reference</span>
                              )}
                            </div>
                          </div>

                          <CapaDetailKvTile iconClass="fas fa-layer-group" label="Module">
                            {detailFormOpts.moduleOption.label}
                          </CapaDetailKvTile>

                          {detail.Module === "study_management" && (
                            <CapaDetailKvTile iconClass="fas fa-flask" label="Study">
                              {detailFormOpts.entityOpt.label || "—"}
                            </CapaDetailKvTile>
                          )}
                          {detail.Module === "user_management" && (
                            <CapaDetailKvTile iconClass="fas fa-user" label="Staff">
                              {detailFormOpts.entityOpt.label || "—"}
                            </CapaDetailKvTile>
                          )}
                          {detail.Module === "isf" && (
                            <>
                              <CapaDetailKvTile iconClass="fas fa-hospital" label="Study (ISF)">
                                {detailFormOpts.isfStudyOpt.label || "—"}
                              </CapaDetailKvTile>
                              <CapaDetailKvTile
                                iconClass="fas fa-folder-open"
                                label="ISF category"
                              >
                                {detailFormOpts.isfCategoryOpt.label || "—"}
                              </CapaDetailKvTile>
                              <CapaDetailKvTile iconClass="fas fa-file-alt" label="Document">
                                {detailFormOpts.selectedDocumentOpt.label || "—"}
                              </CapaDetailKvTile>
                            </>
                          )}

                          <div className="capa-detail-summary__meta">
                            {detail.SourceOrigin && (
                              <div className="capa-detail-summary__meta-item">
                                <i className="fas fa-compass" aria-hidden />
                                <div>
                                  <span className="capa-detail-summary__meta-label">Origin</span>
                                  <span className="capa-detail-summary__meta-value">
                                    {detail.SourceOrigin}
                                  </span>
                                </div>
                              </div>
                            )}
                            <div className="capa-detail-summary__meta-item">
                              <i className="fas fa-user-circle" aria-hidden />
                              <div>
                                <span className="capa-detail-summary__meta-label">Owner</span>
                                <span className="capa-detail-summary__meta-value">
                                  {detail.OwnerName || "—"}
                                </span>
                              </div>
                            </div>
                            <div className="capa-detail-summary__meta-item">
                              <i className="fas fa-plus-circle" aria-hidden />
                              <div>
                                <span className="capa-detail-summary__meta-label">Created</span>
                                <span className="capa-detail-summary__meta-value">
                                  {utils.formatDateTimeDisplay(detail.CreatedAt)}
                                </span>
                              </div>
                            </div>
                            <div className="capa-detail-summary__meta-item">
                              <i className="fas fa-history" aria-hidden />
                              <div>
                                <span className="capa-detail-summary__meta-label">Modified</span>
                                <span className="capa-detail-summary__meta-value">
                                  {utils.formatDateTimeDisplay(detail.ModifiedAt)}
                                </span>
                              </div>
                            </div>
                            <div className="capa-detail-summary__meta-item">
                              <i className="fas fa-flag-checkered" aria-hidden />
                              <div>
                                <span className="capa-detail-summary__meta-label">Target due</span>
                                <span className="capa-detail-summary__meta-value">
                                  {utils.formatDateTimeDisplay(detail.TargetDueDate)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {detail.Status === "Open" &&
                      workflowActions.includes(
                        WORKFLOW_ACTIONS.SUBMIT_INVESTIGATION,
                      ) && (
                      <div className="mt-3 pt-3 border-top d-flex justify-content-end gap-2 flex-wrap">
                        <button
                          type="button"
                          className="btn btn-primary"
                          disabled={busy || !capaDetailsAreComplete(detail)}
                          title={
                            !capaDetailsAreComplete(detail)
                              ? "Add title and description first"
                              : undefined
                          }
                          onClick={() =>
                            handleSubmitForInvestigationTransition()
                          }
                        >
                          Submit for Investigation
                        </button>
                      </div>
                    )}

                    </div>
                  </div>
                    <div className="col-lg-3">
                      <CapaActivityLogPanel auditLog={auditLog} flexFill />
                    </div>
                  </div>

                </div>
              )}

              {selectedStepIndex === 1 && (
                <>
                <div className="capa-detail-summary">
                  <div className="row g-3 capa-detail-summary__layout">
                    <div className="col-12">
                      <div className="capa-investigation-shell d-flex flex-column">
                        <div className="row g-3 g-lg-4 align-items-stretch flex-lg-nowrap capa-investigation-threecol__wrapper">
                          <div className="col-md-9 d-flex flex-column min-h-0">
                            <div className="row g-3 align-items-stretch capa-investigation-main-grid capa-investigation-main-grid--merged">
                          <div className="col-md-9 capa-investigation-threecol__col capa-investigation-main-grid__left">
                            <div className="capa-investigation-threecol__scroll capa-investigation-threecol__scroll--first-section capa-investigation-threecol__left-box flex-grow-1 min-h-0 h-100">
                              <div className="capa-investigation-threecol__scroll-content">
                              <div className="capa-inv-compact mb-3 mb-lg-0">
                                <label
                                  className="form-label mb-2"
                                  htmlFor="capa-investigator-display"
                                >
                                  Investigator{" "}
                                  <span className="text-danger">*</span>
                                  <span className="text-muted fw-normal">
                                    {" "}
                                    (Person responsible for this investigation)
                                  </span>
                                  {loadingStaff && (
                                    <span className="ms-2 text-muted small fw-normal">
                                      Loading…
                                    </span>
                                  )}
                                </label>
                                <div id="capa-investigator-display">
                                  <Dropdown
                                    options={assignedInvestigator ? [assignedInvestigator] : []}
                                    selected={assignedInvestigator}
                                    onSelect={() => {}}
                                    disabled
                                  />
                                </div>
                              </div>

                              <div className="capa-inv-compact">
                                <label className="form-label mb-2">
                                  Investigation details{" "}
                                  <span className="text-danger">*</span>
                                  <span className="text-muted fw-normal">
                                    {" "}
                                    (Summary of findings and context)
                                  </span>
                                </label>
                                {canEditInvestigation ? (
                                  <TextEditor
                                    value={investigationDetails}
                                    onChange={setInvestigationDetails}
                                    placeholder="Enter investigation details..."
                                    height="9rem"
                                    minHeight="6rem"
                                    showImageButton={false}
                                  />
                                ) : (
                                  <div className="text-muted capa-detail-description ql-snow border-0">
                                    <div
                                      className="ql-editor"
                                      style={{ padding: 0 }}
                                      dangerouslySetInnerHTML={{
                                        __html: detail.InvestigationSummary || "",
                                      }}
                                    />
                                  </div>
                                )}
                              </div>
                              </div>

                              {(canEditInvestigation ||
                                (detail.Status === "Under Investigation" &&
                                  workflowActions.includes(
                                    WORKFLOW_ACTIONS.SUBMIT_RCA,
                                  ))) && (
                                <div className="capa-investigation-threecol__actions border-top d-flex justify-content-end gap-2 flex-wrap">
                                  {canEditInvestigation && (
                                    <button
                                      type="button"
                                      className="btn btn-outline-secondary"
                                      disabled={busy || !anyCapaDirty}
                                      title={
                                        !anyCapaDirty
                                          ? "No changes to save"
                                          : undefined
                                      }
                                      onClick={handleUpdateDetail}
                                    >
                                      Save
                                    </button>
                                  )}
                                  {detail.Status === "Under Investigation" &&
                                    workflowActions.includes(
                                      WORKFLOW_ACTIONS.SUBMIT_RCA,
                                    ) && (
                                      <button
                                        type="button"
                                        className="btn btn-primary"
                                        disabled={
                                          busy ||
                                          !investigationComplete ||
                                          anyCapaDirty
                                        }
                                        title={
                                          anyCapaDirty
                                            ? "Save your changes first"
                                            : !investigationComplete
                                              ? "Fill Investigation details and Investigator"
                                              : undefined
                                        }
                                        onClick={handleSubmitInvestigation}
                                      >
                                        Submit for RCA
                                      </button>
                                    )}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="col-md-3 capa-investigation-threecol__col">
                            <div className="capa-investigation-threecol__evidence h-100">
                              {canEditInvestigation ? (
                                <div className="card capa-evidence-card h-100 border shadow-sm d-flex flex-column min-h-0">
                                  <div className="card-header py-2 px-3 bg-white d-flex flex-nowrap align-items-center justify-content-between gap-2 flex-shrink-0">
                                    <span className="fw-semibold text-body mb-0">
                                      Evidences
                                    </span>
                                    <div className="d-flex flex-wrap gap-1">
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() =>
                                          setEvidenceModalMode("full")
                                        }
                                      >
                                        Add
                                      </button>
                                    </div>
                                  </div>
                                  <div className="card-body">
                                    <div className="capa-evidence-list-block d-flex flex-column flex-grow-1 min-h-0">
                                  
                                      <div className="capa-investigation-evidence-table-scroll flex-grow-1 min-h-0">
                                        {renderInvestigationEvidenceList(evidenceTableRows)}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="border rounded p-3 capa-evidence-panel h-100 d-flex flex-column min-h-0">
                                  <h6 className="mb-2 flex-shrink-0">
                                    Supporting evidence
                                  </h6>
                                  <div className="capa-investigation-evidence-table-scroll flex-grow-1 min-h-0">
                                    {renderInvestigationEvidenceList(evidenceTableRows)}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                            </div>
                          </div>

                          <div className="col-md-3 capa-investigation-threecol__col d-flex flex-column">
                            <CapaActivityLogPanel
                              auditLog={auditLog}
                              flexFill
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {canEditInvestigation && evidenceModalMode && (
                  <div
                    className="modal fade show d-block capa-evidence-modal-backdrop"
                    style={{
                      backgroundColor: "rgba(0,0,0,0.45)",
                      zIndex: 1060,
                    }}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="capa-evidence-modal-title"
                  >
                    <div className="modal-dialog modal-dialog-centered modal-md">
                      <div
                        className="modal-content"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="modal-header py-2 px-3">
                          <h5
                            className="modal-title fs-6"
                            id="capa-evidence-modal-title"
                          >
                            {evidenceModalMode === "full"
                              ? "Add evidence"
                              : "Add notes"}
                          </h5>
                          <button
                            type="button"
                            className="btn-close"
                            aria-label="Close"
                            disabled={busy}
                            onClick={closeEvidenceModal}
                          />
                        </div>
                        <div className="modal-body pt-2">
                          {err ? (
                            <div
                              className="alert alert-warning py-2 mb-2 small"
                              role="alert"
                            >
                              {err}
                            </div>
                          ) : null}
                          <div className="capa-evidence-modal-form">
                            {evidenceModalMode === "full" && (
                              <div className="mb-3 capa-evidence-modal-upload">
                                <label className="form-label small mb-1">
                                  File uploader
                                </label>
                                <div className="capa-evidence-upload-slot">
                                  <FileUpload
                                    key={evidenceUploadKey}
                                    className="capa-evidence-fileupload"
                                    multiple
                                    maxFiles={Math.max(
                                      1,
                                      MAX_EVIDENCE_FILES -
                                        evidenceFiles.length,
                                    )}
                                    maxFileSize={2}
                                    returnType="base64"
                                    showPreview={false}
                                    showFileList={false}
                                    disabled={
                                      evidenceFiles.length >= MAX_EVIDENCE_FILES
                                    }
                                    onFileSelect={
                                      handleEvidenceFileUploadSelect
                                    }
                                    onError={handleEvidenceUploadError}
                                  />
                                </div>
                              </div>
                            )}
                            <div className="mb-0">
                              <label className="form-label small mb-1">
                                Notes
                              </label>
                              <textarea
                                className="form-control form-control-sm capa-evidence-notes"
                                rows={3}
                                value={pendingEvidenceNotes}
                                onChange={(e) =>
                                  setPendingEvidenceNotes(e.target.value)
                                }
                                placeholder="Add evidence notes (optional)"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="modal-footer py-2 px-3 gap-2">
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            disabled={busy}
                            onClick={closeEvidenceModal}
                          >
                            Close
                          </button>
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            disabled={busy}
                            onClick={() => void handleAddEvidenceRow()}
                          >
                            {busy ? "Saving…" : "Add & save"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                </>
              )}

              {selectedStepIndex === 2 && (
                <div className="capa-detail-summary">
                  <div className="row g-3 capa-detail-summary__layout">
                    <div className="col-12">
                      <div className="capa-investigation-shell d-flex flex-column">
                        <div className="row g-3 g-lg-4 align-items-stretch flex-lg-nowrap capa-investigation-threecol__wrapper">
                          <div className="col-md-9 d-flex flex-column min-h-0">
                            <div className="row g-3 align-items-stretch capa-investigation-main-grid capa-investigation-main-grid--merged">
                              <div className="col-md-9 capa-investigation-threecol__col capa-investigation-main-grid__left">
                                <div className="capa-investigation-threecol__scroll capa-investigation-threecol__left-box flex-grow-1 min-h-0 h-100">
                                  <div className="capa-investigation-threecol__scroll-content">
                                    <div className="capa-inv-compact mb-3 mb-lg-0">
                                      <label className="form-label mb-2">
                                        RCA method <span className="text-danger">*</span>
                                      </label>
                                      <Dropdown
                                        options={rcaMethodOptions}
                                        selected={rcaMethod}
                                        onSelect={canEditRca ? setRcaMethod : () => {}}
                                        disabled={!canEditRca}
                                      />
                                    </div>

                                    <div className="capa-inv-compact">
                                      <label className="form-label mb-2">
                                        RCA details <span className="text-danger">*</span>
                                      </label>
                                      {canEditRca ? (
                                        <TextEditor
                                          value={rcaDetails}
                                          onChange={setRcaDetails}
                                          placeholder="Enter RCA details..."
                                          height="9rem"
                                          minHeight="6rem"
                                          showImageButton={false}
                                        />
                                      ) : (
                                        <div className="text-muted capa-detail-description ql-snow border-0">
                                          <div
                                            className="ql-editor"
                                            style={{ padding: 0 }}
                                            dangerouslySetInnerHTML={{
                                              __html: detail.RootCauseSummary || "",
                                            }}
                                          />
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {(canEditRca ||
                                    (detail.Status === "Under RCA" &&
                                      workflowActions.includes(
                                        WORKFLOW_ACTIONS.SUBMIT_ACTION_PLAN,
                                      ))) && (
                                    <div className="capa-investigation-threecol__actions border-top d-flex justify-content-end gap-2 flex-wrap">
                                      {canEditRca && (
                                        <button
                                          type="button"
                                          className="btn btn-outline-secondary"
                                          disabled={busy || !anyCapaDirty}
                                          title={
                                            !anyCapaDirty
                                              ? "No changes to save"
                                              : undefined
                                          }
                                          onClick={handleUpdateDetail}
                                        >
                                          Save
                                        </button>
                                      )}
                                      {detail.Status === "Under RCA" &&
                                        workflowActions.includes(
                                          WORKFLOW_ACTIONS.SUBMIT_ACTION_PLAN,
                                        ) && (
                                          <button
                                            type="button"
                                            className="btn btn-primary"
                                            disabled={busy || !rcaComplete || anyCapaDirty}
                                            title={
                                              anyCapaDirty
                                                ? "Save your changes first"
                                                : !rcaComplete
                                                  ? "Select RCA method and enter RCA details"
                                                  : undefined
                                            }
                                            onClick={handleSubmitActionPlan}
                                          >
                                            Submit for Action Plan
                                          </button>
                                        )}
                                    </div>
                                  )}
                                </div>
                              </div>

                              <div className="col-md-3 capa-investigation-threecol__col">
                                <div className="capa-investigation-threecol__evidence h-100">
                                  <div className="card capa-evidence-card h-100 border shadow-sm d-flex flex-column min-h-0">
                                    
                                    <div className="card-header py-2 px-3 bg-white d-flex flex-nowrap align-items-center justify-content-between gap-2 flex-shrink-0">
                                      <span className="fw-semibold text-body mb-0">
                                        RCA Evidences
                                      </span>
                                      {canEditRca && (
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-secondary"
                                          onClick={() => setRcaEvidenceModalMode("full")}
                                        >
                                          Add
                                        </button>
                                      )}
                                    </div>
                                    <div className="card-body">
                                      <div className="capa-investigation-evidence-table-scroll flex-grow-1 min-h-0">
                                        {renderRcaEvidenceList(rcaEvidenceTableRows)}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="col-md-3 capa-investigation-threecol__col d-flex flex-column">
                            <CapaActivityLogPanel auditLog={auditLog} flexFill />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {canEditRca && rcaEvidenceModalMode && (
                <div
                  className="modal fade show d-block capa-evidence-modal-backdrop"
                  style={{
                    backgroundColor: "rgba(0,0,0,0.45)",
                    zIndex: 1060,
                  }}
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="capa-rca-evidence-modal-title"
                >
                  <div className="modal-dialog modal-dialog-centered modal-md">
                    <div
                      className="modal-content"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="modal-header py-2 px-3">
                        <h5
                          className="modal-title fs-6"
                          id="capa-rca-evidence-modal-title"
                        >
                          {rcaEvidenceModalMode === "full"
                            ? "Add evidence"
                            : "Add notes"}
                        </h5>
                        <button
                          type="button"
                          className="btn-close"
                          aria-label="Close"
                          disabled={busy}
                          onClick={closeRcaEvidenceModal}
                        />
                      </div>
                      <div className="modal-body pt-2">
                        {err ? (
                          <div
                            className="alert alert-warning py-2 mb-2 small"
                            role="alert"
                          >
                            {err}
                          </div>
                        ) : null}
                        <div className="capa-evidence-modal-form">
                          {rcaEvidenceModalMode === "full" && (
                            <div className="mb-3 capa-evidence-modal-upload">
                              <label className="form-label small mb-1">
                                File uploader
                              </label>
                              <div className="capa-evidence-upload-slot">
                                <FileUpload
                                  key={rcaEvidenceUploadKey}
                                  className="capa-evidence-fileupload"
                                  multiple
                                  maxFiles={Math.max(
                                    1,
                                    MAX_EVIDENCE_FILES - rcaEvidenceFiles.length,
                                  )}
                                  maxFileSize={2}
                                  returnType="base64"
                                  showPreview={false}
                                  showFileList={false}
                                  disabled={
                                    rcaEvidenceFiles.length >= MAX_EVIDENCE_FILES
                                  }
                                  onFileSelect={handleRcaEvidenceFileUploadSelect}
                                  onError={handleRcaEvidenceUploadError}
                                />
                              </div>
                            </div>
                          )}
                          <div className="mb-0">
                            <label className="form-label small mb-1">
                              Notes
                            </label>
                            <textarea
                              className="form-control form-control-sm capa-evidence-notes"
                              rows={3}
                              value={pendingRcaEvidenceNotes}
                              onChange={(e) =>
                                setPendingRcaEvidenceNotes(e.target.value)
                              }
                              placeholder="Add evidence notes (optional)"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="modal-footer py-2 px-3 gap-2">
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          disabled={busy}
                          onClick={closeRcaEvidenceModal}
                        >
                          Close
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          disabled={busy}
                          onClick={() => void handleAddRcaEvidenceRow()}
                        >
                          {busy ? "Saving…" : "Add & save"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {selectedStepIndex === 4 && (
                <div className="capa-detail-summary mt-3">
                  <div className="row g-3 capa-detail-summary__layout">
                    <div className="col-lg-9 d-flex flex-column min-h-0">
                      <div className="capa-detail-summary__shell" ref={approvalPlanningShellRef}>
                        <div className="d-flex justify-content-end align-items-end mb-2">
                          {canEditApprovalPlan && (
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              onClick={() => setApprovalRecordModalOpen(true)}
                            >
                              Add approver
                            </button>
                          )}
                        </div>
                        <InfoAlert
                          isOpen={approverRecordAlertState.isOpen}
                          onClose={hideApproverRecordAlert}
                          {...approverRecordAlertState}
                        />
                        {canEditApprovalPlan && (
                          <Sidemodal
                            isOpen={approvalRecordModalOpen}
                            onClose={() => setApprovalRecordModalOpen(false)}
                            sidewidth="34rem"
                          >
                            <div className="p-3">
                              <h6 className="mb-3">Add approval assignment</h6>
                              <div className="mb-3">
                                <label className="form-label small mb-1">
                                  Description <span className="text-danger">*</span>
                                </label>
                                <TextEditor
                                  value={newApproverDescription}
                                  onChange={setNewApproverDescription}
                                  placeholder="What should this approver review?"
                                  height="12rem"
                                  minHeight="10rem"
                                  showImageButton={false}
                                />
                              </div>
                              <div className="row g-3">
                                <div className="col-md-6">
                                  <label className="form-label small mb-1">
                                    Due date <span className="text-danger">*</span>
                                  </label>
                                  <DatePicker
                                    className="form-control"
                                    selected={parseYmdToDate(newApproverDueDate)}
                                    onChange={(date) =>
                                      setNewApproverDueDate(formatDateToYmd(date))
                                    }
                                    minDate={todayDate}
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                    dateFormat="dd MMM yyyy"
                                    placeholderText="Select due date"
                                  />
                                </div>
                                <div className="col-md-6">
                                  <label className="form-label small mb-1">
                                    Approver <span className="text-danger">*</span>
                                  </label>
                                  <Dropdown
                                    options={handledStaffOptions}
                                    selected={newApproverStaff}
                                    isMulti={true}
                                    onSelect={(opts) => {
                                      const selected = Array.isArray(opts)
                                        ? opts.filter((o) => o?.value)
                                        : [];
                                      setNewApproverStaff(selected);
                                    }}
                                    disabled={loadingStaff}
                                    showSearch
                                  />
                                </div>
                              </div>
                              <div className="mt-3 d-flex justify-content-end gap-2">
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary"
                                  onClick={() => setApprovalRecordModalOpen(false)}
                                  disabled={addingApproverRecord}
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-primary"
                                  onClick={handleAddApproverRecord}
                                  disabled={addingApproverRecord}
                                >
                                  {addingApproverRecord ? "Adding..." : "Add approver"}
                                </button>
                              </div>
                            </div>
                          </Sidemodal>
                        )}
                        <DataTable
                          data={approverRecordRows}
                          columns={approverRecordColumns}
                          sortable={["DueDate"]}
                          actions={[
                      {
                        label: "Approve",
                        className: "btn-outline-success",
                        visible: (row) => row.CanApprove,
                        disabled: (row) =>
                          addingApproverRecord ||
                          updatingApproverRecordId === row.ApproverRecordId,
                        onClick: (row) =>
                          handleApproverRecordDecision(row.ApproverRecordId, "Accepted"),
                      },
                      {
                        label: "Reject",
                        className: "btn-outline-danger",
                        visible: (row) => row.CanReject,
                        disabled: (row) =>
                          addingApproverRecord ||
                          updatingApproverRecordId === row.ApproverRecordId,
                        onClick: (row) =>
                          handleApproverRecordDecision(row.ApproverRecordId, "Rejected"),
                      },
                      ...(canEditApprovalPlan
                        ? [
                            {
                              label: "Remove",
                              className: "btn-outline-danger",
                              visible: (row) =>
                                getActionItemStatusLabel(row.Status) === "N/A",
                              disabled: (row) =>
                                addingApproverRecord ||
                                updatingApproverRecordId === row.ApproverRecordId,
                              onClick: (row) =>
                                handleDeleteApproverRecord(row.ApproverRecordId),
                            },
                          ]
                        : []),
                          ]}
                        />
                        {canEditApprovalPlan && (
                          <div className="mt-3 d-flex justify-content-end">
                            <div className="d-flex flex-column align-items-end">
                              <button
                                type="button"
                                className="btn btn-primary"
                                disabled={
                                  busy ||
                                  addingApproverRecord ||
                                  Boolean(updatingApproverRecordId) ||
                                  !allApproverRecordsReviewed
                                }
                                title={
                                  !allApproverRecordsReviewed
                                    ? "All approvers must accept or reject first"
                                    : undefined
                                }
                                onClick={handleSendForImplementation}
                              >
                                Send for Implementation
                              </button>
                              {!allApproverRecordsReviewed && (
                                <small className="text-muted mt-1 text-end">
                                  {pendingApproverDecisionCount > 0
                                    ? `${pendingApproverDecisionCount} approver record(s) still pending decision (N/A).`
                                    : "Add approver records and complete all decisions before sending for implementation."}
                                </small>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-lg-3 d-flex flex-column min-h-0">
                      <CapaActivityLogPanel
                        auditLog={auditLog}
                        panelHeight={activityPanelHeight}
                        flexFill
                      />
                    </div>
                  </div>
                </div>
              )}

              {selectedStepIndex > 0 &&
                selectedStepIndex !== 1 &&
                selectedStepIndex !== 2 &&
                selectedStepIndex !== 5 &&
                selectedStepIndex !== 6 &&
                phase &&
                phase.stage !== "Action Planning" &&
                phase.stage !== "Approved" &&
                phase.stage !== "Verification" &&
                phase.stage !== "Closure" && (
                <div className={`card ${border}`}>
                  <div className="card-body py-2 px-3">
                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                      <h6 className="mb-1">{phase.stage}</h6>
                      <span
                        className={`badge ${
                          done ? "bg-success" : current ? "bg-primary" : "bg-secondary"
                        }`}
                      >
                        {done ? "Done" : current ? "Current" : "Upcoming"}
                      </span>
                    </div>
                    <p className="small text-muted mb-2">{phase.purpose}</p>
                    <div className="row small">
                      <div className="col-md-6">
                        <strong className="d-block mb-1">Actions</strong>
                        <ul className="mb-0 ps-3">
                          {phase.actions.map((a) => (
                            <li key={a}>{a}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="col-md-6">
                        <strong className="d-block mb-1">Data to store</strong>
                        <ul className="mb-0 ps-3">
                          {phase.data.map((line) => (
                            <li key={line}>{line}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <p className="small mb-0 mt-2">
                      <strong>Who:</strong> {phase.who}
                    </p>
                    {phase.stage === "Under Investigation" &&
                      detail.InvestigationSummary && (
                        <div className="small mt-2 mb-0 border-top pt-2">
                          <strong>On record</strong>
                          <p className="mb-0">{detail.InvestigationSummary}</p>
                        </div>
                      )}
                    {phase.stage === "Under RCA" &&
                      (detail.RcaMethod || detail.RootCauseSummary) && (
                        <div className="small mt-2 mb-0 border-top pt-2">
                          <strong>On record</strong>
                          {detail.RcaMethod && (
                            <p className="mb-1">
                              RCA method:{" "}
                              {rcaMethodOptionFromStored(detail.RcaMethod, rcaMethodOptions).label}
                            </p>
                          )}
                          {detail.RootCauseSummary && (
                            <div
                              className="mb-0 mt-1 ql-snow"
                              dangerouslySetInnerHTML={{
                                __html: detail.RootCauseSummary,
                              }}
                            />
                          )}
                        </div>
                      )}
                    {phase.stage === "Approved" &&
                      (detail.PlanApprovalStatus ||
                        detail.ApprovedByName ||
                        detail.ApprovalDate) && (
                        <p className="small mt-2 mb-0 border-top pt-2">
                          <strong>On record:</strong>{" "}
                          {detail.PlanApprovalStatus &&
                            `Status: ${detail.PlanApprovalStatus}`}
                          {detail.ApprovedByName && ` · By ${detail.ApprovedByName}`}
                          {detail.ApprovalDate &&
                            ` · ${utils.formatDateTimeDisplay(detail.ApprovalDate)}`}
                        </p>
                      )}
                    {phase.stage === "Implementation" && detail.ImplementationNotes && (
                      <p className="small mt-2 mb-0 border-top pt-2">
                        <strong>On record:</strong> {detail.ImplementationNotes}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {selectedStepIndex === 3 && (
                <div className="capa-detail-summary mt-3">
                  <div className="row g-3 capa-detail-summary__layout">
                    <div className="col-lg-9 d-flex flex-column min-h-0">
                      <div className="capa-detail-summary__shell" ref={actionPlanningShellRef}>
                  <div className="d-flex justify-content-end align-items-end mb-2">
                   
                    {canEditActionPlanning && (
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={() => setActionTaskModalOpen(true)}
                      >
                        Add task
                      </button>
                    )}
                  </div>
                  <InfoAlert
                    isOpen={taskAlertState.isOpen}
                    onClose={hideTaskAlert}
                    {...taskAlertState}
                  />
                  {canEditActionPlanning && (
                    <Sidemodal
                      isOpen={actionTaskModalOpen}
                      onClose={() => setActionTaskModalOpen(false)}
                      sidewidth="34rem"
                    >
                      <div className="p-3">
                        <h6 className="mb-3">Add action item</h6>
                        <div className="mb-3">
                          <label className="form-label small mb-1">
                            Task details <span className="text-danger">*</span>
                          </label>
                          <TextEditor
                            value={newTaskDescription}
                            onChange={setNewTaskDescription}
                            placeholder="Describe the task"
                            height="12rem"
                            minHeight="10rem"
                            showImageButton={false}
                          />
                        </div>
                        <div className="row g-3">
                          <div className="col-md-6">
                            <label className="form-label small mb-1">
                              Task type <span className="text-danger">*</span>
                            </label>
                            <select
                              className="form-select"
                              value={newTaskType}
                              onChange={(e) => setNewTaskType(e.target.value)}
                            >
                              <option value="Corrective">Corrective</option>
                              <option value="Preventive">Preventive</option>
                            </select>
                          </div>
                          <div className="col-md-6">
                            <label className="form-label small mb-1">
                              Assign staff <span className="text-danger">*</span>
                            </label>
                            <Dropdown
                              options={staffDropdownOptions}
                              selected={newTaskApprover}
                              onSelect={(opt) => {
                                if (!opt || opt.value === "") {
                                  setNewTaskApprover(null);
                                } else {
                                  setNewTaskApprover(opt);
                                }
                              }}
                              disabled={loadingStaff}
                              showSearch
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label small mb-1">
                              Start date <span className="text-danger">*</span>
                            </label>
                            <DatePicker
                              className="form-control"
                              selected={parseYmdToDate(newTaskStartDate)}
                              onChange={(date) => {
                                const nextStart = formatDateToYmd(date);
                                setNewTaskStartDate(nextStart);
                                const endDateObj = parseYmdToDate(newTaskEndDate);
                                const startDateObj = parseYmdToDate(nextStart);
                                if (
                                  endDateObj &&
                                  startDateObj &&
                                  endDateObj.getTime() < startDateObj.getTime()
                                ) {
                                  setNewTaskEndDate(nextStart);
                                }
                              }}
                              minDate={todayDate}
                              showMonthDropdown
                              showYearDropdown
                              dropdownMode="select"
                              dateFormat="dd MMM yyyy"
                              placeholderText="Select start date"
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label small mb-1">
                              End date <span className="text-danger">*</span>
                            </label>
                            <DatePicker
                              className="form-control"
                              selected={parseYmdToDate(newTaskEndDate)}
                              onChange={(date) =>
                                setNewTaskEndDate(formatDateToYmd(date))
                              }
                              minDate={
                                (() => {
                                  const startDateObj = parseYmdToDate(newTaskStartDate);
                                  if (!startDateObj) return todayDate;
                                  return startDateObj.getTime() > todayDate.getTime()
                                    ? startDateObj
                                    : todayDate;
                                })()
                              }
                              showMonthDropdown
                              showYearDropdown
                              dropdownMode="select"
                              dateFormat="dd MMM yyyy"
                              placeholderText="Select end date"
                            />
                          </div>
                        </div>
                        <div className="mt-3 d-flex justify-content-end gap-2">
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => setActionTaskModalOpen(false)}
                            disabled={addingTask}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleAddActionItem}
                            disabled={addingTask}
                          >
                            {addingTask ? "Adding..." : "Add task"}
                          </button>
                        </div>
                      </div>
                    </Sidemodal>
                  )}
                  <DataTable
                    data={actionItemRows}
                    columns={actionItemColumns}
                    sortable={["StartDate", "DueDate"]}
                    actions={[
                      {
                        label: "Approve",
                        className: "btn-outline-success",
                        visible: (row) => row.CanApprove,
                        disabled: (row) =>
                          addingTask || updatingTaskId === row.ActionItemId,
                        onClick: (row) =>
                          handleTaskDecision(row.ActionItemId, "Accepted"),
                      },
                      {
                        label: "Reject",
                        className: "btn-outline-danger",
                        visible: (row) => row.CanReject,
                        disabled: (row) =>
                          addingTask || updatingTaskId === row.ActionItemId,
                        onClick: (row) =>
                          handleTaskDecision(row.ActionItemId, "Rejected"),
                      },
                      ...(canEditActionPlanning
                        ? [
                            {
                              label: "Remove",
                              className: "btn-outline-danger",
                              visible: (row) =>
                                getActionItemStatusLabel(row.Status) === "N/A",
                              disabled: (row) =>
                                addingTask || updatingTaskId === row.ActionItemId,
                              onClick: (row) => handleDeleteTask(row.ActionItemId),
                            },
                          ]
                        : []),
                    ]}
                  />
                  {canEditActionPlanning && (
                    <div className="mt-3 d-flex justify-content-end">
                      <div className="d-flex flex-column align-items-end">
                        <button
                          type="button"
                          className="btn btn-primary"
                          disabled={busy || addingTask || !allTasksReviewed}
                          title={
                            !allTasksReviewed
                              ? "All tasks must be Accepted or Rejected first"
                              : undefined
                          }
                          onClick={handleSendForApproval}
                        >
                          Send for Approval
                        </button>
                        {!allTasksReviewed && (
                          <small className="text-muted mt-1">
                            {pendingTaskDecisionCount > 0
                              ? `${pendingTaskDecisionCount} task(s) still pending decision (N/A).`
                              : "Add and review tasks before sending for approval."}
                          </small>
                        )}
                      </div>
                    </div>
                  )}
                      </div>
                    </div>
                    <div className="col-lg-3 d-flex flex-column min-h-0">
                      <CapaActivityLogPanel
                        auditLog={auditLog}
                        panelHeight={activityPanelHeight}
                        flexFill
                      />
                    </div>
                  </div>
                </div>
              )}

              {selectedStepIndex === 5 && (
                <div className="capa-detail-summary mt-3">
                  <div className="row g-3 capa-detail-summary__layout">
                    <div className="col-lg-9 d-flex flex-column min-h-0">
                      <div
                        className="capa-detail-summary__shell"
                        ref={implementationShellRef}
                      >
                        <div className="d-flex justify-content-end align-items-end mb-2">
                          {canEditImplementation && (
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              onClick={() => setImplTaskModalOpen(true)}
                            >
                              Add implementation task
                            </button>
                          )}
                        </div>
                        <InfoAlert
                          isOpen={implTaskAlertState.isOpen}
                          onClose={hideImplTaskAlert}
                          {...implTaskAlertState}
                        />
                        {canEditImplementation && (
                          <Sidemodal
                            isOpen={implTaskModalOpen}
                            onClose={() => setImplTaskModalOpen(false)}
                            sidewidth="34rem"
                          >
                            <div className="p-3">
                              <h6 className="mb-3">Add implementation task</h6>
                              <div className="mb-3">
                                <label className="form-label small mb-1">
                                  Details <span className="text-danger">*</span>
                                </label>
                                <TextEditor
                                  value={newImplDescription}
                                  onChange={setNewImplDescription}
                                  placeholder="Describe the implementation work"
                                  height="12rem"
                                  minHeight="10rem"
                                  showImageButton={false}
                                />
                              </div>
                              <div className="row g-3">
                                <div className="col-md-6">
                                  <label className="form-label small mb-1">
                                    Due date <span className="text-danger">*</span>
                                  </label>
                                  <DatePicker
                                    className="form-control"
                                    selected={parseYmdToDate(newImplDueDate)}
                                    onChange={(date) =>
                                      setNewImplDueDate(formatDateToYmd(date))
                                    }
                                    minDate={todayDate}
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                    dateFormat="dd MMM yyyy"
                                    placeholderText="Select due date"
                                  />
                                </div>
                                <div className="col-md-6">
                                  <label className="form-label small mb-1">
                                    Staff <span className="text-danger">*</span>
                                  </label>
                                  <Dropdown
                                    options={handledStaffOptions}
                                    selected={newImplStaff}
                                    onSelect={(opt) => {
                                      if (!opt || opt.value === "") {
                                        setNewImplStaff(null);
                                      } else {
                                        setNewImplStaff(opt);
                                      }
                                    }}
                                    disabled={loadingStaff}
                                    showSearch
                                  />
                                </div>
                              </div>
                              <div className="mt-3 d-flex justify-content-end gap-2">
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary"
                                  onClick={() => setImplTaskModalOpen(false)}
                                  disabled={addingImplTask}
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-primary"
                                  onClick={handleAddImplementationTask}
                                  disabled={addingImplTask}
                                >
                                  {addingImplTask ? "Adding..." : "Add task"}
                                </button>
                              </div>
                            </div>
                          </Sidemodal>
                        )}
                        <DataTable
                          data={implementationTaskRows}
                          columns={implementationTaskColumns}
                          sortable={["DueDate"]}
                          actions={[
                            {
                              label: "Save status",
                              className: "btn-primary",
                              visible: (row) => {
                                if (!row.CanUpdateStatus) return false;
                                if (row.ImplementationStatusCompletedLocked) {
                                  return false;
                                }
                                const draft =
                                  implStatusDraft[row.ImplementationTaskId] ??
                                  row.ImplementationStatus;
                                return draft !== row.ImplementationStatus;
                              },
                              disabled: (row) =>
                                addingImplTask ||
                                updatingImplTaskId === row.ImplementationTaskId,
                              onClick: (row) =>
                                handleSaveImplementationStatus(
                                  row.ImplementationTaskId,
                                ),
                            },
                            ...(canEditImplementation
                              ? [
                                  {
                                    label: "Remove",
                                    className: "btn-outline-danger",
                                    visible: (row) => row.CanRemove,
                                    disabled: (row) =>
                                      addingImplTask ||
                                      updatingImplTaskId ===
                                        row.ImplementationTaskId,
                                    onClick: (row) =>
                                      handleDeleteImplementationTask(
                                        row.ImplementationTaskId,
                                      ),
                                  },
                                ]
                              : []),
                          ]}
                        />
                        {canEditImplementation &&
                          workflowActions.includes(
                            WORKFLOW_ACTIONS.SEND_TO_VERIFICATION,
                          ) && (
                            <div className="mt-3 d-flex justify-content-end">
                              <div className="d-flex flex-column align-items-end">
                                <button
                                  type="button"
                                  className="btn btn-primary"
                                  disabled={
                                    busy ||
                                    addingImplTask ||
                                    Boolean(updatingImplTaskId) ||
                                    !allImplementationTasksReadyForVerification
                                  }
                                  title={
                                    !allImplementationTasksReadyForVerification
                                      ? "All tasks must be Completed, Partially Completed, or Deferred (not Not Started or In Progress)"
                                      : undefined
                                  }
                                  onClick={handleSendToVerification}
                                >
                                  Send to Verification
                                </button>
                                {!allImplementationTasksReadyForVerification && (
                                  <small className="text-muted mt-1 text-end">
                                    {implementationTasks.length === 0
                                      ? "Add at least one implementation task before sending to Verification."
                                      : (() => {
                                          const ns =
                                            pendingImplementationNotStartedCount;
                                          const ip =
                                            pendingImplementationInProgressCount;
                                          const parts = [];
                                          if (ns > 0) {
                                            parts.push(
                                              `${ns} task(s) still Not Started`,
                                            );
                                          }
                                          if (ip > 0) {
                                            parts.push(
                                              `${ip} task(s) still In Progress`,
                                            );
                                          }
                                          return parts.length
                                            ? `${parts.join(" and ")}.`
                                            : "Complete implementation statuses before sending to Verification.";
                                        })()}
                                  </small>
                                )}
                              </div>
                            </div>
                          )}
                      </div>
                    </div>
                    <div className="col-lg-3 d-flex flex-column min-h-0">
                      <CapaActivityLogPanel
                        auditLog={auditLog}
                        panelHeight={activityPanelHeight}
                        flexFill
                      />
                    </div>
                  </div>
                </div>
              )}

              {selectedStepIndex === 6 && (
                <div className="capa-detail-summary mt-3">
                  <div className="row g-3 capa-detail-summary__layout">
                    <div className="col-lg-9 d-flex flex-column min-h-0">
                      <div
                        className="capa-detail-summary__shell"
                        ref={verificationShellRef}
                      >
                        <div className="d-flex justify-content-end align-items-end mb-2">
                          {canEditVerification && (
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              onClick={() => setVerificationRequestModalOpen(true)}
                            >
                              Add verification request
                            </button>
                          )}
                        </div>
                        <InfoAlert
                          isOpen={verificationAlertState.isOpen}
                          onClose={hideVerificationAlert}
                          {...verificationAlertState}
                        />
                        {canEditVerification && (
                          <Sidemodal
                            isOpen={verificationRequestModalOpen}
                            onClose={() => setVerificationRequestModalOpen(false)}
                            sidewidth="34rem"
                          >
                            <div className="p-3">
                              <h6 className="mb-3">Add verification request</h6>
                              <div className="mb-3">
                                <label className="form-label small mb-1">
                                  Instructions for verifier{" "}
                                  <span className="text-danger">*</span>
                                </label>
                                <TextEditor
                                  value={newVerReqDescription}
                                  onChange={setNewVerReqDescription}
                                  placeholder="What should the verifier check?"
                                  height="12rem"
                                  minHeight="10rem"
                                  showImageButton={false}
                                />
                              </div>
                              <div className="row g-3">
                                <div className="col-md-6">
                                  <label className="form-label small mb-1">
                                    Due date <span className="text-danger">*</span>
                                  </label>
                                  <DatePicker
                                    className="form-control"
                                    selected={parseYmdToDate(newVerReqDueDate)}
                                    onChange={(date) =>
                                      setNewVerReqDueDate(formatDateToYmd(date))
                                    }
                                    minDate={todayDate}
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                    dateFormat="dd MMM yyyy"
                                    placeholderText="Select due date (required)"
                                  />
                                </div>
                                <div className="col-md-6">
                                  <label className="form-label small mb-1">
                                    Staff <span className="text-danger">*</span>
                                  </label>
                                  <Dropdown
                                    options={handledStaffOptions}
                                    selected={newVerReqStaff}
                                    onSelect={(opt) => {
                                      if (!opt || opt.value === "") {
                                        setNewVerReqStaff(null);
                                      } else {
                                        setNewVerReqStaff(opt);
                                      }
                                    }}
                                    disabled={loadingStaff}
                                    showSearch
                                  />
                                </div>
                              </div>
                              <div className="mt-3 d-flex justify-content-end gap-2">
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary"
                                  onClick={() =>
                                    setVerificationRequestModalOpen(false)
                                  }
                                  disabled={addingVerReq}
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-primary"
                                  onClick={handleAddVerificationRequest}
                                  disabled={addingVerReq}
                                >
                                  {addingVerReq ? "Adding..." : "Add request"}
                                </button>
                              </div>
                            </div>
                          </Sidemodal>
                        )}
                        {verificationRows.length === 0 ? (
                          <div className="text-muted small py-4 text-center border rounded bg-light">
                            No verification requests yet. Use the Add verification
                            request button above.
                          </div>
                        ) : (
                          <div className="capa-verification-requests">
                            {verificationRows.map((row) => {
                              const rid = row.VerificationRequestId;
                              const assigneeName = row.AssignedStaffLabel || "—";
                              const requesterName = row.RequestedByLabel || "—";
                              const attachFiles =
                                row.RequestStatus === "Submitted"
                                  ? parseEvidenceFiles(
                                      row.VerificationAttachments,
                                    )
                                  : [];
                              const attachCount = attachFiles.length;
                              const attachSummary =
                                row.RequestStatus !== "Submitted"
                                  ? null
                                  : attachCount > 0
                                    ? `${attachCount} file(s)`
                                    : "None";
                              const attachListPlain =
                                attachCount > 0
                                  ? attachFiles
                                      .map(
                                        (f) =>
                                          String(f.name || "").trim() || "File",
                                      )
                                      .join(", ")
                                  : "";
                              const commentsPlain = String(
                                row.VerifierComments || "",
                              ).trim();
                              const statusLabel = row.StatusLabel;
                              const statusClass =
                                statusLabel === "Effective"
                                  ? "bg-success"
                                  : statusLabel === "Not Effective"
                                    ? "bg-danger"
                                    : "bg-warning text-dark";
                              const instructionPlain = stripHtml(
                                row.Description || "",
                              ).trim();
                              const createdDateTimeDisplay = row.RequestedAt
                                ? utils.formatDateTimeDisplay(row.RequestedAt)
                                : "—";
                              return (
                                <div
                                  key={rid}
                                  className="capa-detail-summary__activity-item capa-vreq-card"
                                >
                                  <div className="capa-detail-summary__activity-top">
                                    <span
                                      className={`badge rounded-pill ${statusClass}`}
                                    >
                                      {statusLabel}
                                    </span>
                                    <div className="capa-vreq-card__actions">
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() =>
                                          setVerificationDetailOpenId(rid)
                                        }
                                      >
                                        View
                                      </button>
                                      {row.CanVerify && (
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-primary"
                                          disabled={
                                            Boolean(submittingVerificationId) ||
                                            addingVerReq
                                          }
                                          onClick={() =>
                                            setVerificationVerifyOpenId(rid)
                                          }
                                        >
                                          Verify
                                        </button>
                                      )}
                                      {canEditVerification &&
                                        row.RequestStatus === "Pending" && (
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-outline-danger"
                                            disabled={addingVerReq}
                                            onClick={() =>
                                              handleDeleteVerificationRequest(
                                                rid,
                                              )
                                            }
                                          >
                                            Remove
                                          </button>
                                        )}
                                    </div>
                                  </div>
                                  <hr className="capa-vreq-card__rule" />
                                  <div className="capa-vreq-card__row capa-vreq-card__row--split">
                                    <div className="min-w-0">
                                      <div className="capa-detail-summary__activity-field-caption">
                                        Assignee
                                      </div>
                                      <div className="capa-detail-summary__activity-user-cell min-w-0 mt-1">
                                        <span
                                          className="capa-detail-summary__activity-user-avatar capa-list-owner-avatar"
                                          style={{
                                            backgroundColor:
                                              getAvatarColor(assigneeName),
                                          }}
                                          title={assigneeName}
                                        >
                                          {getInitials(assigneeName)}
                                        </span>
                                        <span className="capa-detail-summary__activity-field-name text-truncate d-inline-block">
                                          {assigneeName}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="capa-vreq-card__col-end min-w-0 text-md-end">
                                      <div className="capa-detail-summary__activity-field-caption">
                                        Due date
                                      </div>
                                      <div className="capa-detail-summary__activity-field-name mt-1">
                                        {formatVerificationDateOnly(row.DueDate)}
                                      </div>
                                    </div>
                                  </div>
                                  <hr className="capa-vreq-card__rule" />
                                  <div className="capa-detail-summary__activity-change capa-vreq-card__stack">
                                    <div className="capa-detail-summary__activity-field-caption">
                                      Instructions for verifier
                                    </div>
                                    {instructionPlain ? (
                                      <HoverPreview
                                        className="capa-detail-summary__activity-new"
                                        {...capaRichHoverPreviewProps(
                                          row.Description || "",
                                        )}
                                        anchorShowHtml={false}
                                        previewLength={99999}
                                        appendEllipsis={false}
                                        maxWidth={280}
                                        maxHeight={220}
                                      />
                                    ) : (
                                      <span className="capa-detail-summary__activity-old">
                                        —
                                      </span>
                                    )}
                                    <div className="capa-detail-summary__activity-field-caption">
                                      Verifier comments
                                    </div>
                                    {commentsPlain ? (
                                      <HoverPreview
                                        className="capa-detail-summary__activity-new"
                                        {...capaRichHoverPreviewProps(commentsPlain)}
                                        anchorShowHtml={false}
                                        previewLength={99999}
                                        appendEllipsis={false}
                                        maxWidth={280}
                                        maxHeight={200}
                                      />
                                    ) : (
                                      <span className="capa-detail-summary__activity-old">
                                        —
                                      </span>
                                    )}
                                    <div className="capa-detail-summary__activity-field-caption">
                                      Attachments
                                    </div>
                                    {attachSummary == null ? (
                                      <span className="capa-detail-summary__activity-old">
                                        —
                                      </span>
                                    ) : attachCount === 0 ? (
                                      <span className="capa-detail-summary__activity-old">
                                        None
                                      </span>
                                    ) : (
                                      <HoverPreview
                                        className="capa-detail-summary__activity-new"
                                        content={attachListPlain}
                                        plainTextForPreview={attachSummary}
                                        previewLength={32}
                                        maxWidth={280}
                                        maxHeight={200}
                                      />
                                    )}
                                  </div>
                                  <div
                                    className="capa-detail-summary__activity-footer capa-vreq-card__footer"
                                    aria-label={`Requested by ${requesterName}, created ${createdDateTimeDisplay}`}
                                  >
                                    <div className="capa-detail-summary__activity-user-cell min-w-0 flex-grow-1">
                                      <span
                                        className="capa-detail-summary__activity-user-avatar capa-list-owner-avatar"
                                        style={{
                                          backgroundColor:
                                            getAvatarColor(requesterName),
                                        }}
                                        title={requesterName}
                                      >
                                        {getInitials(requesterName)}
                                      </span>
                                      <span className="capa-detail-summary__activity-field-name text-truncate">
                                        {requesterName}
                                      </span>
                                    </div>
                                    <span className="capa-detail-summary__activity-time">
                                      {createdDateTimeDisplay}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {canEditVerification && (
                          <div className="mt-3 d-flex flex-column align-items-end gap-2">
                            <button
                              type="button"
                              className="btn btn-primary"
                              disabled={busy || !canSendForClosure}
                              title={
                                !verificationReadyForClosure
                                  ? "Complete verification (at least one effective outcome, no pending requests) before sending for closure."
                                  : undefined
                              }
                              onClick={handleSendForClosure}
                            >
                              Send for closure
                            </button>
                            {!verificationReadyForClosure &&
                              verificationRequests.length > 0 && (
                                <small className="text-muted text-end">
                                  Finish verification requests (effective outcome,
                                  no pending) to enable Send for closure.
                                </small>
                              )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-lg-3 d-flex flex-column min-h-0">
                      <CapaActivityLogPanel
                        auditLog={auditLog}
                        panelHeight={activityPanelHeight}
                        flexFill
                      />
                    </div>
                  </div>
                </div>
              )}

              {selectedStepIndex === closureStageIdx && (
                <div className="capa-detail-summary mt-3 capa-detail-summary--closure">
                  <div className="row g-3 capa-detail-summary__layout">
                    <div
                      className="col-lg-9 capa-closure-main-column d-flex flex-column min-h-0 pe-lg-3"
                      ref={closureShellRef}
                    >
                      <div className="capa-closure-left-panel d-flex flex-column flex-grow-1 min-h-0 overflow-hidden border rounded-2 bg-white shadow-sm">
                        <div className="capa-closure-scroll min-h-0 px-3 pt-2 pb-1">
                          <div className="capa-closure-section">
                          {detail.Status === "Closed" ? (
                            <div className="mb-2">
                              <div className="px-0 pt-0">
                                <div className="capa-evidence-panel pt-1">
                                  <label className="form-label fw-semibold mb-2">
                                    Final remarks
                                  </label>
                                  <div className="text-muted capa-detail-description ql-snow border-0">
                                    <div
                                      className="ql-editor"
                                      style={{ padding: 0 }}
                                      dangerouslySetInnerHTML={{
                                        __html: detail.ClosureRemarks || "",
                                      }}
                                    />
                                  </div>
                                  <h6 className="mb-2 mt-3">Attachments</h6>
                                  <DataTable
                                    data={closureEvidenceTableRows}
                                    columns={evidenceTableColumns}
                                    actions={closureEvidenceReadOnlyTableActions}
                                  />
                                </div>
                              </div>
                            </div>
                          ) : detail.Status === "Closure" &&
                            selectedStepIndex === closureStageIdx &&
                            canEditClosure ? (
                            <div className="mb-0 d-flex flex-column min-h-0 capa-closure-editable-wrap">
                              <div className="px-0 pt-0 d-flex flex-column min-h-0">
                                <div className="capa-evidence-panel pt-1 capa-evidence-panel--closure-stretch">
                                  <label className="form-label fw-semibold mb-2 flex-shrink-0">
                                    Final remarks{" "}
                                    <span className="text-danger">*</span>
                                  </label>
                                  <div className="capa-closure-remarks-grow">
                                    <TextEditor
                                      className="capa-closure-texteditor"
                                      value={closureRemarks}
                                      onChange={setClosureRemarks}
                                      placeholder="Enter final remarks..."
                                      height="10.5rem"
                                      minHeight="9rem"
                                      showImageButton={false}
                                    />
                                  </div>
                                  <label className="form-label small mb-2 flex-shrink-0">
                                    Attachments (optional)
                                  </label>
                                  <div className="row g-2 align-items-stretch capa-closure-attach-row">
                                    <div className="col-12 col-md-6">
                                      <FileUpload
                                        key={closureEvidenceUploadKey}
                                        className="capa-evidence-fileupload capa-closure-fileupload"
                                        multiple
                                        maxFiles={Math.max(
                                          1,
                                          MAX_EVIDENCE_FILES -
                                            closureEvidenceFiles.length,
                                        )}
                                        maxFileSize={2}
                                        returnType="base64"
                                        showPreview={false}
                                        showFileList={false}
                                        disabled={
                                          closureEvidenceFiles.length >=
                                          MAX_EVIDENCE_FILES
                                        }
                                        onFileSelect={
                                          handleClosureEvidenceFileSelect
                                        }
                                        onError={
                                          handleClosureEvidenceUploadError
                                        }
                                      />
                                    </div>
                                    <div className="col-12 col-md-6">
                                      <div className="capa-verifier-attach-panel h-100">
                                        {closureEvidenceFiles.length > 0 ? (
                                          <ul className="list-unstyled mb-0 capa-verifier-attach-list">
                                            {closureEvidenceFiles.map((f) => (
                                              <li
                                                key={f.id}
                                                className="d-flex align-items-start gap-2"
                                              >
                                                <div className="flex-grow-1 min-w-0">
                                                  <div className="capa-verifier-attach-list__title text-break fw-medium">
                                                    {f.name || "—"}
                                                  </div>
                                                  <div className="capa-verifier-attach-list__size text-muted">
                                                    {f.size != null
                                                      ? formatFileSize(f.size)
                                                      : "—"}
                                                  </div>
                                                </div>
                                                <button
                                                  type="button"
                                                  className="btn btn-link p-0 text-danger flex-shrink-0 align-self-start capa-verifier-attach-list__delete"
                                                  onClick={() => {
                                                    confirmRemoveClosureEvidenceFile(
                                                      f.id,
                                                    );
                                                  }}
                                                  aria-label="Delete attachment"
                                                  title="Delete"
                                                >
                                                  <i
                                                    className="fas fa-trash-alt"
                                                    aria-hidden
                                                  />
                                                </button>
                                              </li>
                                            ))}
                                          </ul>
                                        ) : (
                                          <p className="small text-muted mb-0">
                                            No attachments yet.
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : selectedStepIndex === closureStageIdx &&
                            detail.Status === "Verification" ? (
                            <p className="text-muted small mb-0">
                              Click <strong>Send for closure</strong> on the
                              Verification step after verification is complete to
                              move this CAPA to Closure and enter final remarks.
                            </p>
                          ) : (
                            <p className="text-muted small mb-0">
                              Closure content is available when the CAPA is in the
                              Closure stage.
                            </p>
                          )}
                          </div>
                        </div>
                      {detail.Status === "Closure" && canEditClosure ? (
                        <div className="flex-shrink-0 pt-2 pb-3 px-3 d-flex justify-content-end gap-2 flex-wrap capa-closure-actions border-top bg-white">
                          <button
                            type="button"
                            className="btn btn-primary"
                            disabled={busy || !canCompleteClosure}
                            onClick={handleCloseCapa}
                          >
                            {busy ? "Closing..." : "Close CAPA"}
                          </button>
                        </div>
                      ) : null}
                      </div>
                    </div>
                    <div className="col-lg-3 d-flex flex-column min-h-0">
                      <CapaActivityLogPanel
                        auditLog={auditLog}
                        panelHeight={activityPanelHeight}
                        flexFill
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {verificationDetailOpenId &&
        (() => {
          const req = verificationRequests.find(
            (x) => x.VerificationRequestId === verificationDetailOpenId,
          );
          if (!req) return null;
          const assigneeName = req.AssignedName || "—";
          const requesterName = req.RequestedByName || "—";
          const attachFiles = parseEvidenceFiles(req.VerificationAttachments);
          const statusLabel =
            req.RequestStatus === "Submitted" && req.Outcome === "Effective"
              ? "Effective"
              : req.RequestStatus === "Submitted" && req.Outcome === "Not Effective"
                ? "Not Effective"
                : "Open";
          const statusClassModal =
            statusLabel === "Effective"
              ? "bg-success"
              : statusLabel === "Not Effective"
                ? "bg-danger"
                : "bg-warning text-dark";
          const attachCountModal = attachFiles.length;
          const instructionPlainModal = stripHtml(
            String(req.Description || ""),
          ).trim();
          const commentsPlainModal = stripHtml(
            String(req.VerifierComments || ""),
          ).trim();
          const createdDateTimeDisplay = req.RequestedAt
            ? utils.formatDateTimeDisplay(req.RequestedAt)
            : "—";
          return (
            <Sidemodal
              isOpen={Boolean(verificationDetailOpenId)}
              onClose={() => setVerificationDetailOpenId(null)}
              sidewidth="40rem"
            >
              <div className="p-3 capa-verification-detail-modal">
                <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                  <h6 className="mb-0">Verification request details</h6>
                  <span className={`badge rounded-pill ${statusClassModal}`}>
                    {statusLabel}
                  </span>
                </div>
                <hr className="capa-vreq-card__rule" />
                <div className="row g-3 small mb-2">
                  <div className="col-12 col-md-6 min-w-0">
                    <div className="capa-detail-summary__activity-field-caption">
                      Assignee
                    </div>
                    <div className="d-flex align-items-center gap-2 min-w-0 mt-1">
                      <div
                        className="capa-list-owner-avatar flex-shrink-0"
                        style={{
                          backgroundColor: getAvatarColor(assigneeName),
                        }}
                        title={assigneeName}
                      >
                        {getInitials(assigneeName)}
                      </div>
                      <span className="text-truncate">{assigneeName}</span>
                    </div>
                  </div>
                  <div className="col-12 col-md-6 min-w-0 text-md-end">
                    <div className="capa-detail-summary__activity-field-caption">
                      Due date
                    </div>
                    <div className="capa-detail-summary__activity-field-name mt-1">
                      {formatVerificationDateOnly(req.DueDate)}
                    </div>
                  </div>
                </div>
                <div className="capa-detail-summary__activity-change capa-vreq-card__stack min-w-0">
                  <div className="capa-detail-summary__activity-field-caption">
                    Instructions for verifier
                  </div>
                  {instructionPlainModal ? (
                    <div className="capa-verification-detail-modal__rich-scroll">
                      {renderCapaStoredRichText(req.Description)}
                    </div>
                  ) : (
                    <span className="capa-detail-summary__activity-old">—</span>
                  )}
                  <div className="capa-detail-summary__activity-field-caption">
                    Verifier comments
                  </div>
                  {commentsPlainModal ? (
                    <div className="capa-verification-detail-modal__rich-scroll">
                      {renderCapaStoredRichText(req.VerifierComments)}
                    </div>
                  ) : (
                    <span className="capa-detail-summary__activity-old">—</span>
                  )}
                  <div className="capa-detail-summary__activity-field-caption">
                    Attachments
                  </div>
                  {req.RequestStatus !== "Submitted" ? (
                    <span className="capa-detail-summary__activity-old">—</span>
                  ) : attachCountModal === 0 ? (
                    <p className="small text-muted mb-0">No attachments.</p>
                  ) : (
                    <div className="capa-verifier-attach-panel">
                      <ul className="list-unstyled mb-0 capa-verifier-attach-list">
                        {attachFiles.map((f, idx) => {
                          const fid = f.id != null ? String(f.id) : `att-${idx}`;
                          const dataUrl = f.dataUrl != null ? String(f.dataUrl) : "";
                          return (
                            <li
                              key={fid}
                              className="d-flex align-items-start gap-2"
                            >
                              <div className="flex-grow-1 min-w-0">
                                <div className="capa-verifier-attach-list__title text-break fw-medium">
                                  {String(f.name || "").trim() || "File"}
                                </div>
                                <div className="capa-verifier-attach-list__size text-muted">
                                  {f.size != null
                                    ? formatFileSize(f.size)
                                    : "—"}
                                </div>
                              </div>
                              {dataUrl ? (
                                <button
                                  type="button"
                                  className="btn btn-link p-0 flex-shrink-0 align-self-start capa-verifier-attach-list__view"
                                  title="View"
                                  aria-label="View attachment"
                                  onClick={() =>
                                    window.open(
                                      dataUrl,
                                      "_blank",
                                      "noopener,noreferrer",
                                    )
                                  }
                                >
                                  <i className="fas fa-eye" aria-hidden />
                                </button>
                              ) : null}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
                <div
                  className="capa-detail-summary__activity-footer capa-vreq-card__footer capa-verification-detail-modal__footer mt-3 pt-3"
                  aria-label={`Requested by ${requesterName}, created ${createdDateTimeDisplay}`}
                >
                  <div className="capa-detail-summary__activity-user-cell min-w-0 flex-grow-1">
                    <span
                      className="capa-detail-summary__activity-user-avatar capa-list-owner-avatar"
                      style={{
                        backgroundColor: getAvatarColor(requesterName),
                      }}
                      title={requesterName}
                    >
                      {getInitials(requesterName)}
                    </span>
                    <span className="capa-detail-summary__activity-field-name text-truncate">
                      {requesterName}
                    </span>
                  </div>
                  <span className="capa-detail-summary__activity-time">
                    {createdDateTimeDisplay}
                  </span>
                </div>
              </div>
            </Sidemodal>
          );
        })()}

      {verificationVerifyOpenId &&
        (() => {
          const req = verificationRequests.find(
            (x) => x.VerificationRequestId === verificationVerifyOpenId,
          );
          if (!req) return null;
          const rid = req.VerificationRequestId;
          const draft = verifierDraft[rid] || {
            outcome: "",
            comments: "",
            files: [],
          };
          return (
            <div
              className="modal fade show d-block"
              style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1060 }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="capa-verification-modal-title"
              onClick={(e) => {
                if (e.target === e.currentTarget) handleCloseVerificationModal();
              }}
            >
              <div className="modal-dialog modal-dialog-centered modal-lg capa-verification-modal">
                <div
                  className="modal-content d-flex flex-column"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="modal-header border-0 pb-2 flex-shrink-0">
                    <h5
                      className="modal-title"
                      id="capa-verification-modal-title"
                    >
                      Complete verification
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      aria-label="Close"
                      disabled={Boolean(submittingVerificationId)}
                      onClick={handleCloseVerificationModal}
                    />
                  </div>
                  <div className="modal-body pt-0 capa-verification-modal__body">
                    <div className="small text-muted mb-3">
                      <strong>Instructions:</strong>
                      <div className="mt-1 text-body">
                        {renderCapaStoredRichText(req.Description)}
                      </div>
                    </div>
                    <div className="mb-3">
                      <span className="small fw-semibold d-block mb-1">
                        Outcome <span className="text-danger">*</span>
                      </span>
                      {CAPA_VERIFICATION_OUTCOME_OPTIONS.map((opt) => (
                        <div
                          key={opt.value}
                          className="form-check form-check-inline"
                        >
                          <input
                            className="form-check-input"
                            type="radio"
                            name={`ver-outcome-${rid}`}
                            id={`ver-outcome-${rid}-${opt.value}`}
                            checked={draft.outcome === opt.value}
                            onChange={() =>
                              setVerifierDraft((prev) => ({
                                ...prev,
                                [rid]: {
                                  ...(prev[rid] || {
                                    comments: "",
                                    files: [],
                                  }),
                                  outcome: opt.value,
                                },
                              }))
                            }
                          />
                          <label
                            className="form-check-label small"
                            htmlFor={`ver-outcome-${rid}-${opt.value}`}
                          >
                            {opt.label}
                          </label>
                        </div>
                      ))}
                    </div>
                    <label className="form-label small mb-1">Comments</label>
                    <textarea
                      className="form-control mb-3"
                      rows={3}
                      value={draft.comments}
                      onChange={(e) =>
                        setVerifierDraft((prev) => ({
                          ...prev,
                          [rid]: {
                            ...(prev[rid] || {
                              outcome: "",
                              files: [],
                            }),
                            comments: e.target.value,
                          },
                        }))
                      }
                      placeholder="Verification comments"
                    />
                    <label className="form-label small mb-2">
                      Attachments (optional)
                    </label>
                    <div className="row g-3 align-items-start">
                      <div className="col-12 col-md-6">
                        <FileUpload
                          key={verifierUploadKey[rid] || 0}
                          returnType="base64"
                          maxFileSize={25}
                          onFileSelect={handleVerifierFileSelect(rid)}
                          onError={handleVerifierUploadError}
                          showFileList={false}
                          showPreview={false}
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <div className="capa-verifier-attach-panel h-100">
                          {draft.files.length > 0 ? (
                            <ul className="list-unstyled mb-0 capa-verifier-attach-list">
                              {draft.files.map((f) => (
                                <li
                                  key={f.id}
                                  className="d-flex align-items-start gap-2"
                                >
                                  <div className="flex-grow-1 min-w-0">
                                    <div className="capa-verifier-attach-list__title text-break fw-medium">
                                      {f.name || "—"}
                                    </div>
                                    <div className="capa-verifier-attach-list__size text-muted">
                                      {f.size != null
                                        ? formatFileSize(f.size)
                                        : "—"}
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    className="btn btn-link p-0 text-danger flex-shrink-0 align-self-start capa-verifier-attach-list__delete"
                                    onClick={() =>
                                      handleRemoveVerifierFile(rid, f.id)
                                    }
                                    aria-label="Delete attachment"
                                    title="Delete"
                                  >
                                    <i
                                      className="fas fa-trash-alt"
                                      aria-hidden
                                    />
                                  </button>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="small text-muted mb-0">
                              No attachments yet.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer flex-shrink-0 border-top bg-light">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={Boolean(submittingVerificationId)}
                      onClick={handleCloseVerificationModal}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={
                        submittingVerificationId === rid || addingVerReq
                      }
                      onClick={() => handleSubmitVerificationResponse(rid)}
                    >
                      {submittingVerificationId === rid
                        ? "Submitting..."
                        : "Submit verification"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      <ConfirmModal
        isOpen={modalState.isOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        title={modalState.title}
        message={modalState.message}
        confirmText={modalState.confirmText}
        cancelText={modalState.cancelText}
        confirmButtonClass={modalState.confirmButtonClass}
        icon={modalState.icon}
      />
    </div>
  );
};

export default CapaDetail;
